import type { CaseManifestV1, LayerASpec } from "@cold-case/shared/case-manifest";
import type { AccusationPhaseState } from "@cold-case/shared/accusation-phase";
import { MIN_EVIDENCE_FOR_ACCUSATION } from "@cold-case/shared/constants";
import Phaser from "phaser";
import { io, type Socket } from "socket.io-client";

import { RANDOM_CHARACTER_ID, isRandomCharacterId } from "@cold-case/shared/playable-character";

import { createPreGameController, type PreGameController } from "./app/pre-game.js";
import {
  hideAllDeferredLoading,
  hideDeferredLoading,
  hideSessionLoading,
  isGameLoadingOverlayVisible,
  showDeferredLoading,
  showSessionLoading,
  type LoadingHandle,
} from "./game/world-loading.js";
import { buildPlayerManualHtml } from "./content/player-manual-html.js";
import { buildInteriorContentPlan } from "./game/building-interior-flow.js";
import { GameScene } from "./game/GameScene.js";
import { purgeCaseTextures } from "./game/reset-case-runtime.js";
import { UIScene } from "./game/UIScene.js";
import {
  buildingLeadDescription,
  prepareClientCaseManifest,
  resolveBuildingDisplayName,
  zoneDisplayName,
} from "./lib/building-display.js";
import {
  buildingClearanceJournalPatch,
  userBuildingClearanceSet,
} from "./lib/building-clearance.js";
import {
  resolveBuildingInvestigation,
  type BuildingInvestigationSurface,
} from "./lib/building-investigation.js";
import type { CoherenceJournal } from "./lib/dialog-parser.js";
import { substituteDialog } from "./lib/dialog-parser.js";
import {
  closeNarrativeModal,
  isNarrativeModalOpen,
  openNarrativeModal,
} from "./lib/narrative-modal.js";
import { closeZoneTransitConfirm } from "./lib/zone-transit-confirm.js";
import { renderCaseBriefHtml } from "./lib/case-brief-html.js";
import {
  closeCaseRoulette,
  RouletteAbortedError,
  runCaseRoulette,
} from "./lib/case-roulette-overlay.js";
import { readCollapsedClueIds, setClueCollapsed } from "./lib/clue-ui-state.js";
import {
  isStaleSessionFetch,
  shouldBlockLobbyAutoStart,
} from "./lib/lobby-stale-session.js";
import { npcRoleLabelForPlayer } from "./lib/npc-case-notes.js";
import type { NpcDialogCloseResult } from "./lib/npc-dialog-session.js";
import { api, getToken, setToken } from "./net.js";
import { gameSocketOptions, wireConnectionLifecycle } from "./net-lifecycle.js";
import { dismissClueToast, showClueDiscoveryToast } from "./game/clue-toast.js";
import {
  appendDispatchMessage,
  formatClueDiscoveryDispatch,
  resetRadioDispatch,
  setRadioElapsedSec,
} from "./lib/radio-dispatch.js";
import {
  onInvestigationTimerTick,
  resetInvestigationNudges,
} from "./lib/investigation-nudges.js";
import {
  getRadioTab,
  isRadioOverlayOpen,
  onGameRadioMessage,
  setRadioOpen,
  setRadioTabFromMirror,
  toggleRadioFromHud,
  wireRadioOverlay,
} from "./lib/radio-overlay.js";
import {
  briefSeenStorageKey,
  hasSeenCaseBrief,
  shouldPersistBriefSeenOnJournalClose,
} from "./lib/case-boot.js";
import {
  JOURNAL_ACCUSE_TAB_INDEX,
  JOURNAL_BRIEF_TAB_INDEX,
  JOURNAL_CLUES_TAB_INDEX,
  JOURNAL_MAP_TAB_INDEX,
  JOURNAL_NOTES_TAB_INDEX,
  JOURNAL_SUSPECTS_TAB_INDEX,
  JOURNAL_TAB_LABELS,
  JOURNAL_TIMELINE_TAB_INDEX,
} from "./game/journal-tab-indices.js";
import {
  clueDisplayName,
  formatCaseNoteAge,
  formatElapsedClock,
  resolveCaseDisplayTitle,
  npcDisplayName,
  resolveFoundAtDisplay,
  relatedNpcDisplayName,
  triangulationConfirmLabel,
} from "./lib/manifest-display.js";
import { npcSouthPortraitImgHtml } from "./lib/npc-portrait-html.js";
import {
  buildMapPoiList,
  buildMinimapPayload,
  findNearestMapPoiAtCanvas,
  isValidWorldWaypoint,
  mapPoiDisplayLabel,
  paintMinimapCanvas,
  partitionMapPois,
  resolveMapWaypoint,
  type MapPoi,
  type MapWaypointTarget,
  type MinimapPlayerBlip,
} from "./game/case-files-map.js";
import {
  focusPlayfieldCanvas,
  isNpcConversationOverlayOpen,
  releaseDomOverlayFocus,
  resetPlayerInputOverlays,
  setPlayerControlsRestoredListener,
  setPlayerOverlayOpen,
} from "./lib/player-input-gate.js";
import {
  ensureGlobalCaptureEnabled,
  wirePhaserKeyboardForDomTyping,
} from "./lib/phaser-keyboard-typing.js";
import {
  mountInGameOverlaysToShell,
  unmountInGameOverlaysFromShell,
} from "./lib/in-game-overlays.js";
import {
  bindVisualViewportSync,
  readGameHostDocumentOffset,
  readGameHostLayoutRect,
  syncVisualViewportBounds,
} from "./lib/visual-viewport-bounds.js";
import {
  bindAccusationController,
  closeAccusationOverlays,
  initAccusationController,
  accusationBlockedMessage,
  isAccusationPhaseActive,
  onAccusationRadioMessage,
  renderMultiplayerAccuseTabHtml,
  resumeAccusationPhase,
  startAccusationFromCaseFile,
  teardownAccusationController,
} from "./game/accusation-controller.js";
import type { SessionSyncPayload } from "@cold-case/shared/session-sync";
import { applySessionSyncPayload } from "./lib/apply-session-sync.js";
import {
  createSessionPollController,
  fetchSessionSync,
  sessionSyncFieldsForPollMode,
  type SessionPollController,
} from "./lib/session-poll.js";
import type { LeaderMirrorPayload, LeaderMirrorNpc } from "@cold-case/shared/player-follow";
import { closeFollowConfirm } from "./lib/follow-confirm.js";
import { hideNarrativeNudgeUi } from "./lib/follow-nudge-ui.js";
import {
  emitLeaderMirror,
  initLeaderFollowMirror,
  disposeLeaderFollowMirror,
  leaderHasFollowers,
  setLeaderFollowMirrorSession,
  setOnFollowersAttached,
} from "./game/leader-follow-mirror.js";
import {
  disposePlayerFollow,
  endFollowMode,
  initPlayerFollow,
  isFollowingLeader,
  rebindPlayerFollowSocket,
  requestStopFollowing,
} from "./game/player-follow.js";
import { worldLabelForPlayer } from "./game/world-nameplate.js";

function dismissNpcConversationUi(): void {
  if (!isNpcConversationOverlayOpen()) return;
  game?.events.emit("ccc_force_close_npc_dialog");
}

type SessionPayload = {
  sessionId: string;
  status: string;
  playerCount: number;
  caseManifest: CaseManifestV1;
  layerA: LayerASpec;
  players: {
    userId: string;
    displayName: string;
    characterNpcId: string;
    zone?: string;
    tileX?: number;
    tileY?: number;
  }[];
  journal: Record<string, unknown>;
  accusationPhase?: AccusationPhaseState | null;
  result: { correct: boolean; solution_summary: string } | null;
  startedAt: string;
};

type AccuseResult = {
  correct: boolean;
  solution_summary: string;
};

let game: Phaser.Game | null = null;
let unbindVisualViewport: (() => void) | null = null;
let phaserShellWired = false;
let prefetchedSession: { sid: string; data: SessionPayload } | null = null;
let prefetchInflight: Promise<SessionPayload | null> | null = null;
let sessionDomAbort: AbortController | null = null;
let currentSocket: Socket | null = null;
let sessionId = "";
let userId = "";
let currentLobbyCode = "";
let startingSessionId = "";
let caseRouletteSessionId = "";
let lobbyStartRetryTimer: ReturnType<typeof setTimeout> | null = null;

type CaseStartReveal = {
  chosenTitle: string;
  catalog: { total: number; poolTotal?: number; titles: string[] };
};
const lobbyStartBlockedSessionIds = new Set<string>();
let worldReadyHideLoader: (() => void) | null = null;
let caseBootGeneration = 0;
let investigationBootFinishedGen = 0;
let bootWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
const BOOT_WATCHDOG_MS = 15_000;
let caseRuntimeResetChain: Promise<void> = Promise.resolve();
let caseResolved = false;
/** Movement frozen when case is closed. */
let investigationLocked = false;
/** Investigation elapsed seconds (HUD clock; solo pauses while reading). */
let investigationElapsedSec = 0;
let journalUiActiveTab = -1;
let lastNotesAgeRefreshMinute = -1;
let lastAccuseResult: AccuseResult | null = null;
let refreshJournalUi: (() => void) | null = null;
let refreshLobbyView: ((code: string) => void) | null = null;
let preGame: PreGameController | null = null;
let lastKnownClueIds = new Set<string>();
let currentGameZone: "downtown" | "maple_heights" = "downtown";
let mapTabViewZone: "downtown" | "maple_heights" = "downtown";
let mapBlipRefreshTimer: ReturnType<typeof setInterval> | null = null;

function clearMapBlipRefreshTimer(): void {
  if (mapBlipRefreshTimer) {
    clearInterval(mapBlipRefreshTimer);
    mapBlipRefreshTimer = null;
  }
}
let selectedMapPoiId: string | null = null;
let mapBuildingsCollapsed = false;
let mapNpcsCollapsed = false;
let sessionPlayerCount = 1;
let sessionManifest: CaseManifestV1 | null = null;
let sessionPlayerNames: string[] = [];
let sessionPlayersList: SessionPayload["players"] = [];

const REJOIN_STORAGE_KEY = "ccc_active_case";

let connectionBannerEl: HTMLElement | null = null;
let connectionBannerActive = false;
let connectionBannerShowTimer: ReturnType<typeof setTimeout> | null = null;
const CONNECTION_BANNER_DEBOUNCE_MS = 2500;
let pendingPostCaseOutcome: "win" | "loss" | null = null;

const BANNER_CONNECTION_STYLE =
  "display:none;position:fixed;top:0;left:0;right:0;z-index:60;padding:6px 12px;padding-top:max(6px,env(safe-area-inset-top,0px));background:#4a1520;color:#ffc8c8;font-size:0.85rem;text-align:center;pointer-events:none;";
let gameStatusBannerEl: HTMLElement | null = null;
let sessionPollController: SessionPollController | null = null;
const HEAL_DEBOUNCE_MS = 400;
let healDebounceTimer: ReturnType<typeof setTimeout> | null = null;
let healInFlight = false;

function asCoherenceJournal(j: Record<string, unknown>): CoherenceJournal {
  const clearanceRaw = j.building_clearance_by_user;
  const building_clearance_by_user =
    clearanceRaw && typeof clearanceRaw === "object"
      ? (clearanceRaw as CoherenceJournal["building_clearance_by_user"])
      : undefined;
  return {
    discovered_clues: (j.discovered_clues as string[]) ?? [],
    talked_to_npcs: (j.talked_to_npcs as string[]) ?? [],
    visited_buildings: (j.visited_buildings as string[]) ?? [],
    building_clearance_by_user,
  };
}

function buildingInvestigationOpts(ccc: CccRegistry | undefined): {
  userId: string;
  playerCount: number;
} {
  return {
    userId: userId,
    playerCount: ccc?.playerCount ?? sessionPlayerCount,
  };
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function displayOrUnknown(value: string | null | undefined): string {
  const t = String(value ?? "").trim();
  return t.length > 0 ? t : "(Unknown)";
}

function playerOrderedNames(
  players: SessionPayload["players"],
  displayNames: string[]
): string[] {
  const idx = players.findIndex((p) => p.userId === userId);
  if (idx <= 0) return displayNames;
  const ordered = [...displayNames];
  const self = ordered.splice(idx, 1)[0];
  if (self) ordered.unshift(self);
  return ordered;
}

function formatApiError(data: unknown, fallback: string): string {
  if (!data || typeof data !== "object") return fallback;
  const d = data as Record<string, unknown>;
  if (typeof d.hint === "string" && d.hint.trim()) return d.hint;
  if (d.error === "no_playable_seeds") {
    return "No playable case seeds left. Generate and validate seeds on the server, then try again.";
  }
  if (d.error === "seed_manifest_missing") {
    return "Case seed files are missing on the server. Ask the operator to run seed generation.";
  }
  if (
    d.error === "case_load_failed" &&
    typeof d.message === "string" &&
    d.message.includes("layerA_coherence")
  ) {
    return "This case needs a fresh map layout. Leave the lobby, host a new game from the hub, and try again.";
  }
  if (typeof d.message === "string" && d.message.trim()) return d.message;
  if (d.error === "case_load_timeout" || d.error === "client_timeout") {
    return "Case layout is taking longer than usual — wait a moment and try starting again.";
  }
  if (d.error === "start_in_progress") {
    return "The host is still starting the case — wait a moment and try again.";
  }
  if (d.error === "lobby_full") return "That room is full.";
  if (d.error === "not_found") return "Room not found — check the code.";
  if (d.error === "character_taken") return "Another detective already picked that character.";
  if (d.error === "insufficient_evidence") {
    const min = typeof d.minimum === "number" ? d.minimum : minEvidenceFromContext();
    return `Select at least ${min} evidence clue(s) before submitting.`;
  }
  if (d.error === "missing_killer") return "Choose a suspect before submitting.";
  if (d.error === "undiscovered_suspect") {
    return "You must discover that suspect in the world before accusing them.";
  }
  if (d.error === "wrong_player_count") {
    return "Everyone must be in the room before the host can start.";
  }
  if (d.error === "character_not_selected") {
    return "Every player must pick a detective before starting.";
  }
  if (d.error === "playable_roster_incomplete") {
    return "Playable character art is missing on the server. Run npm run assets:sync-playable-characters.";
  }
  if (d.error === "not_host") return "Only the host can start the case.";
  if (d.error === "invalid_credentials") return "Incorrect password.";
  if (d.error === "guest_forbidden") return "This action is not available for guest accounts.";
  if (typeof d.error === "string") return String(d.error).replace(/_/g, " ");
  return fallback;
}

function minEvidenceFromContext(): number {
  return MIN_EVIDENCE_FOR_ACCUSATION;
}

function saveRejoinHint(
  lobbyCode: string,
  sid: string,
  zone: "downtown" | "maple_heights" = "downtown"
): void {
  try {
    localStorage.setItem(
      REJOIN_STORAGE_KEY,
      JSON.stringify({
        lobbyCode,
        sessionId: sid,
        zone,
        ...(userId ? { userId } : {}),
      })
    );
  } catch {
    /* ignore quota */
  }
}

function clearRejoinHint(): void {
  try {
    localStorage.removeItem(REJOIN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function readRejoinHint(): {
  lobbyCode: string;
  sessionId: string;
  zone: "downtown" | "maple_heights";
} | null {
  try {
    const raw = localStorage.getItem(REJOIN_STORAGE_KEY);
    if (!raw) return null;
    const o = JSON.parse(raw) as {
      lobbyCode?: string;
      sessionId?: string;
      zone?: string;
      userId?: string;
    };
    if (!o.lobbyCode || !o.sessionId) return null;
    if (o.userId && userId && o.userId !== userId) {
      clearRejoinHint();
      return null;
    }
    const zone = o.zone === "maple_heights" ? "maple_heights" : "downtown";
    return { lobbyCode: o.lobbyCode, sessionId: o.sessionId, zone };
  } catch {
    /* ignore */
  }
  return null;
}

function ensureGameStatusBanner(): HTMLElement {
  if (gameStatusBannerEl?.isConnected) return gameStatusBannerEl;
  const banner = document.createElement("div");
  banner.id = "game-status-banner";
  banner.style.cssText =
    "display:none;position:fixed;top:max(48px,env(safe-area-inset-top,0px));left:50%;transform:translateX(-50%);z-index:55;width:min(560px,calc(100vw - 24px));max-width:calc(100vw - 24px);padding:10px 14px;background:#3d2814;border:1px solid #ffb86c;border-radius:8px;color:#ffe4c4;font-size:0.95rem;line-height:1.45;text-align:center;box-sizing:border-box;";
  document.body.append(banner);
  gameStatusBannerEl = banner;
  return banner;
}

const FOLLOW_BLOCK_MS = 5000;
const FOLLOW_BLOCK_MSG = "Stop following to control your detective.";

function blockIfFollowingLeader(message = FOLLOW_BLOCK_MSG): boolean {
  if (!isFollowingLeader()) return false;
  showGameStatusMsg(message, FOLLOW_BLOCK_MS);
  return true;
}

function showGameStatusMsg(text: string, ms = 4000): void {
  const banner = ensureGameStatusBanner();
  banner.textContent = text;
  banner.style.display = "block";
  window.setTimeout(() => {
    if (banner.textContent === text) banner.style.display = "none";
  }, ms);
}

function ensureConnectionBanner(): HTMLElement {
  if (connectionBannerEl?.isConnected) return connectionBannerEl;
  const banner = document.createElement("div");
  banner.id = "connection-banner";
  banner.style.cssText = BANNER_CONNECTION_STYLE;
  document.body.append(banner);
  connectionBannerEl = banner;
  return banner;
}

function syncTopBannerVisibility(): void {
  const banner = ensureConnectionBanner();
  banner.style.display = connectionBannerActive ? "block" : "none";
}

function clearPostCaseBanner(): void {
  pendingPostCaseOutcome = null;
}

function consumePostCaseOutcome(): "win" | "loss" | null {
  const o = pendingPostCaseOutcome;
  pendingPostCaseOutcome = null;
  return o;
}

function setPostCaseBanner(won: boolean | null): void {
  if (won === null) {
    pendingPostCaseOutcome = null;
    return;
  }
  pendingPostCaseOutcome = won ? "win" : "loss";
}

function setConnectionBanner(visible: boolean, text = "Connection lost — reconnecting…"): void {
  const banner = ensureConnectionBanner();
  if (!visible) {
    if (connectionBannerShowTimer) {
      clearTimeout(connectionBannerShowTimer);
      connectionBannerShowTimer = null;
    }
    connectionBannerActive = false;
    banner.style.display = "none";
    syncTopBannerVisibility();
    recomputeSessionPollMode();
    return;
  }
  const showNow = () => {
    connectionBannerActive = true;
    banner.style.cssText = BANNER_CONNECTION_STYLE;
    banner.textContent = text;
    banner.style.display = "block";
    syncTopBannerVisibility();
    recomputeSessionPollMode();
  };
  if (connectionBannerShowTimer) clearTimeout(connectionBannerShowTimer);
  const debounce =
    text.includes("Connection lost") || text.includes("reconnecting when the network");
  if (debounce) {
    connectionBannerShowTimer = setTimeout(() => {
      connectionBannerShowTimer = null;
      showNow();
    }, CONNECTION_BANNER_DEBOUNCE_MS);
  } else {
    showNow();
  }
}

function teardownLobbySocket(): void {
  const sock = currentSocket;
  currentSocket = null;
  if (!sock) return;
  sock.removeAllListeners();
  sock.disconnect();
}

/** Drop in-memory game session when entering a lobby (avoids heal/poll on a stale id). */
function clearStaleGameSessionState(): void {
  if (startingSessionId) return;
  sessionId = "";
  stopSessionPoll();
  invalidateSessionPrefetch();
  if (healDebounceTimer) {
    clearTimeout(healDebounceTimer);
    healDebounceTimer = null;
  }
  healInFlight = false;
  setConnectionBanner(false);
}

function isActiveGameSession(): boolean {
  return (
    !!sessionId &&
    (game?.scene.isActive("GameScene") ?? false) &&
    !el("game-host").classList.contains("hidden")
  );
}

const SESSION_EXPIRED_MSG = "This case expired after everyone left.";

function blockLobbySessionStart(sid: string): void {
  if (sid) lobbyStartBlockedSessionIds.add(sid);
}

function isLobbySessionStartBlocked(sid: string): boolean {
  return shouldBlockLobbyAutoStart(sid, lobbyStartBlockedSessionIds);
}

function clearLobbySessionStartBlocks(): void {
  lobbyStartBlockedSessionIds.clear();
}

function dismissAllGameLoading(): void {
  clearBootWatchdog();
  hideSessionLoading();
  hideAllDeferredLoading();
}

function signalPlayfieldReadyAfterBoot(): void {
  const journalEl = document.getElementById("journal-overlay");
  const briefOpen = journalEl?.classList.contains("open") ?? false;
  if (
    !caseResolved &&
    !investigationLocked &&
    !briefOpen &&
    !isAccusationPhaseActive() &&
    !isNpcConversationOverlayOpen()
  ) {
    focusPlayfieldCanvas();
    ensureGlobalCaptureEnabled();
    game?.events.emit("ccc_player_controls_restored");
  }
}

function clearBootWatchdog(): void {
  if (bootWatchdogTimer) {
    clearTimeout(bootWatchdogTimer);
    bootWatchdogTimer = null;
  }
}

function finishInvestigationBoot(g: Phaser.Game, bootGen: number): void {
  if (bootGen !== caseBootGeneration) return;
  if (investigationBootFinishedGen === bootGen) return;
  investigationBootFinishedGen = bootGen;
  clearBootWatchdog();
  const boot = (
    g.registry.get("ccc") as {
      bootMeta?: { alreadyResolved: boolean; result: AccuseResult | null };
    }
  )?.bootMeta;
  if (boot?.alreadyResolved) {
    dismissAllGameLoading();
    markCaseResolved();
    if (boot.result) {
      lastAccuseResult = boot.result;
      showOutcome(boot.result);
    }
    return;
  }
  if (isAccusationPhaseActive()) {
    dismissAllGameLoading();
    return;
  }
  ensureFirstCaseBriefOpened?.();
  dismissAllGameLoading();
  signalPlayfieldReadyAfterBoot();
  if (isGameLoadingOverlayVisible()) {
    console.warn(
      "[ccc] investigation boot: loading overlay still active after dismissAllGameLoading"
    );
  }
}

function scheduleBootWatchdog(g: Phaser.Game, bootGen: number): void {
  clearBootWatchdog();
  bootWatchdogTimer = setTimeout(() => {
    bootWatchdogTimer = null;
    if (bootGen !== caseBootGeneration) return;
    if (!isGameLoadingOverlayVisible()) return;
    if (!game?.scene.isActive("GameScene")) {
      void resetGameToLobby("Could not load the case — try hosting again.");
      return;
    }
    console.warn("[ccc] boot watchdog: forcing investigation boot completion");
    finishInvestigationBoot(g, bootGen);
  }, BOOT_WATCHDOG_MS);
}

async function postLobbyLeaveQuiet(): Promise<void> {
  const code = currentLobbyCode;
  if (!code) return;
  const res = await api(`/api/lobbies/${encodeURIComponent(code)}/leave`, {
    method: "POST",
  }).catch(() => ({ ok: false as const, status: 0, data: null }));
  if (!res.ok && res.status !== 404 && res.status !== 403) {
    /* lobby may already be deleted after abandon-on-create */
  }
  currentLobbyCode = "";
  teardownLobbySocket();
  setConnectionBanner(false);
}

async function performCaseRuntimeReset(opts: {
  leaveLobby?: boolean;
  hideGameHost?: boolean;
  clearSession?: boolean;
} = {}): Promise<void> {
  caseBootGeneration += 1;
  investigationBootFinishedGen = 0;
  clearBootWatchdog();
  dismissAllGameLoading();
  closeCaseRoulette();
  caseRouletteSessionId = "";
  teardownAccusationController();
  resetRadioDispatch();
  resetInvestigationNudges();
  stopSessionPoll();
  invalidateSessionPrefetch();
  dismissResumePrompt();
  if (healDebounceTimer) {
    clearTimeout(healDebounceTimer);
    healDebounceTimer = null;
  }
  healInFlight = false;
  if (game) prepareCaseSceneBoot(game);
  else stopGameScenes();
  parkPhaserRenderer();
  destroyPhaserGame();
  sessionDomAbort?.abort();
  sessionDomAbort = null;
  if (opts.clearSession !== false) {
    sessionId = "";
    startingSessionId = "";
    caseResolved = false;
    investigationLocked = false;
  }
  if (opts.leaveLobby) {
    await postLobbyLeaveQuiet();
  }
  if (opts.hideGameHost !== false) {
    hideGameHost();
  }
}

function scheduleCaseRuntimeReset(
  run: () => Promise<void>
): Promise<void> {
  caseRuntimeResetChain = caseRuntimeResetChain.then(run);
  return caseRuntimeResetChain;
}

function cancelInFlightCaseBoot(): void {
  if (!startingSessionId && !sessionId && !caseRouletteSessionId) return;
  caseBootGeneration += 1;
  investigationBootFinishedGen = 0;
  dismissAllGameLoading();
  closeCaseRoulette();
  caseRouletteSessionId = "";
  if (game) prepareCaseSceneBoot(game);
  else stopGameScenes();
  parkPhaserRenderer();
  destroyPhaserGame();
  startingSessionId = "";
  sessionId = "";
  hideGameHost();
}

function bindWorldReadyHideLoader(g: Phaser.Game, bootGen: number): void {
  if (worldReadyHideLoader) {
    g.events.off("world_ready", worldReadyHideLoader);
  }
  worldReadyHideLoader = () => {
    finishInvestigationBoot(g, bootGen);
  };
  g.events.once("world_ready", worldReadyHideLoader);
}

function scheduleTryStartFromLobbyRetry(sid: string): void {
  if (lobbyStartRetryTimer) clearTimeout(lobbyStartRetryTimer);
  lobbyStartRetryTimer = setTimeout(() => {
    lobbyStartRetryTimer = null;
    if (sessionId === sid && game?.scene.isActive("GameScene")) return;
    void tryStartFromLobby(sid);
  }, 500);
}

async function abandonStaleLobbySession(
  lobbyCode: string,
  message = SESSION_EXPIRED_MSG
): Promise<void> {
  clearRejoinHint();
  dismissResumePrompt();
  clearPostCaseBanner();
  setConnectionBanner(false);
  stopSessionPoll();
  dismissAllGameLoading();
  cancelInFlightCaseBoot();
  sessionId = "";
  startingSessionId = "";
  invalidateSessionPrefetch();
  if (lobbyStartRetryTimer) {
    clearTimeout(lobbyStartRetryTimer);
    lobbyStartRetryTimer = null;
  }
  if (healDebounceTimer) {
    clearTimeout(healDebounceTimer);
    healDebounceTimer = null;
  }
  healInFlight = false;
  const code = lobbyCode || currentLobbyCode;
  if (code) {
    await api(`/api/lobbies/${encodeURIComponent(code)}/leave`, { method: "POST" }).catch(
      () => undefined
    );
  }
  teardownLobbySocket();
  currentLobbyCode = "";
  hideGameHost();
  await showSignedInHub(message);
}

function handleSessionExpired(message = SESSION_EXPIRED_MSG): void {
  clearRejoinHint();
  dismissResumePrompt();
  clearPostCaseBanner();
  setConnectionBanner(false);
  stopSessionPoll();
  dismissAllGameLoading();
  const inGame = isActiveGameSession();
  sessionId = "";
  startingSessionId = "";
  invalidateSessionPrefetch();
  if (healDebounceTimer) {
    clearTimeout(healDebounceTimer);
    healDebounceTimer = null;
  }
  healInFlight = false;
  if (inGame) {
    void resetGameToLobby(message);
    return;
  }
  if (currentLobbyCode) {
    void abandonStaleLobbySession(currentLobbyCode, message);
    return;
  }
  ensurePreGame().setMessage(message);
}

async function checkSessionAvailable(sid: string): Promise<boolean> {
  if (!sid || isLobbySessionStartBlocked(sid)) return false;
  const s = await api<SessionPayload>(`/api/sessions/${encodeURIComponent(sid)}`);
  if (s.ok) return true;
  const err = (s.data as { error?: string })?.error;
  if (isStaleSessionFetch(s.status, err)) {
    blockLobbySessionStart(sid);
    return false;
  }
  return false;
}

function applySessionSyncFromServer(data: SessionSyncPayload): void {
  applySessionSyncPayload(data, {
    caseResolved,
    applyJournal: applyJournalFromServer,
    resumeAccusationPhase,
    isAccusationPhaseActive,
    showOutcome,
  });
}

function recomputeSessionPollMode(): void {
  if (!sessionPollController || !sessionId || caseResolved) {
    sessionPollController?.stop();
    return;
  }
  if (document.hidden) {
    sessionPollController.setMode("background");
    return;
  }
  if (connectionBannerActive) {
    sessionPollController.setMode("reconnecting");
    return;
  }
  if (!currentSocket?.connected) {
    sessionPollController.setMode("offline");
    return;
  }
  if (isAccusationPhaseActive()) {
    sessionPollController.setMode("accusation");
    return;
  }
  sessionPollController.setMode("normal");
}

function sessionSyncFieldsForCurrentPoll(): "full" | "minimal" {
  if (!sessionPollController) {
    return currentSocket?.connected ? "minimal" : "full";
  }
  return sessionSyncFieldsForPollMode(sessionPollController.getMode());
}

async function syncSessionFromServer(
  fields?: import("@cold-case/shared/session-sync").SessionSyncFields
): Promise<boolean> {
  if (!sessionId || caseResolved) return false;
  const s = await fetchSessionSync(sessionId, fields ?? sessionSyncFieldsForCurrentPoll());
  if (!s.ok) {
    if (s.status === 404 || s.status === 403 || s.error === "not_found" || s.error === "forbidden") {
      handleSessionExpired();
    }
    return false;
  }
  applySessionSyncFromServer(s.data);
  recomputeSessionPollMode();
  return true;
}

function wireGameSessionEndedListener(): void {
  if (!currentSocket) return;
  currentSocket.off("game:session_ended");
  currentSocket.on("game:session_ended", () => {
    handleSessionExpired();
  });
}

function wireGameJoinedHandler(): void {
  if (!currentSocket) return;
  currentSocket.off("game:joined");
  currentSocket.on(
    "game:joined",
    (p: {
      ok: boolean;
      error?: string;
      status?: string;
      result?: AccuseResult | null;
      accusationPhase?: AccusationPhaseState | null;
    }) => {
      if (!p.ok) {
        if (p.error === "not_found") {
          handleSessionExpired();
        } else if (p.error === "forbidden") {
          setConnectionBanner(true, "Reconnecting to this case…");
          scheduleHealGameConnection();
        } else {
          showGameStatusMsg("Could not join this case session.");
        }
        return;
      }
      setConnectionBanner(false);
      if (p.status === "resolved" && p.result) {
        showOutcome(p.result);
      } else {
        if (p.accusationPhase) resumeAccusationPhase(p.accusationPhase);
        else if (isAccusationPhaseActive()) resumeAccusationPhase(null);
      }
      if (sessionPlayerCount >= 2 && currentSocket) {
        bindAccusationController(currentSocket);
      }
      if (game?.scene.isActive("GameScene")) {
        ensurePhaserGame().registry.events.emit("ccc_force_pose_emit");
      }
    }
  );
}

function scheduleHealGameConnection(): void {
  if (!sessionId || caseResolved || startingSessionId) return;
  if (healDebounceTimer) clearTimeout(healDebounceTimer);
  healDebounceTimer = setTimeout(() => {
    healDebounceTimer = null;
    void healGameConnection();
  }, HEAL_DEBOUNCE_MS);
}

const HEAL_IN_FLIGHT_TIMEOUT_MS = 12_000;

async function healGameConnection(): Promise<void> {
  if (!sessionId || caseResolved || startingSessionId) return;
  if (healInFlight) return;
  healInFlight = true;
  const healStarted = Date.now();
  const healWatchdog = setTimeout(() => {
    if (healInFlight) {
      console.warn("[ccc] healGameConnection timed out after", HEAL_IN_FLIGHT_TIMEOUT_MS, "ms");
      healInFlight = false;
    }
  }, HEAL_IN_FLIGHT_TIMEOUT_MS);
  const wasOffline = !currentSocket?.connected;
  try {
    if (currentSocket && !currentSocket.connected) currentSocket.connect();
    if (!currentSocket?.connected) return;
    currentSocket.emit("game:join", sessionId);
    syncLiveSocketToPhaser();
    const ok = await syncSessionFromServer("full");
    if (!sessionId || !ok) return;
    if (wasOffline && game?.scene.isActive("GameScene")) {
      showGameStatusMsg("Back online — you're reconnected.", 2500);
    }
    ensurePhaserGame().registry.events.emit("ccc_force_pose_emit");
  } finally {
    clearTimeout(healWatchdog);
    if (Date.now() - healStarted < HEAL_IN_FLIGHT_TIMEOUT_MS) {
      healInFlight = false;
    }
  }
}

let lifecycleWired = false;

function beaconAbandonLobby(): void {
  if (isActiveGameSession()) return;
  cancelInFlightCaseBoot();
  const code = currentLobbyCode;
  if (!code) {
    preGame?.resetLobbyState();
    return;
  }
  const t = getToken();
  if (t) {
    void fetch(`/api/lobbies/${encodeURIComponent(code)}/leave`, {
      method: "POST",
      headers: { Authorization: `Bearer ${t}` },
      keepalive: true,
    }).catch(() => undefined);
  }
  currentLobbyCode = "";
  teardownLobbySocket();
  setConnectionBanner(false);
  preGame?.resetLobbyState();
}

async function abandonCurrentLobby(): Promise<void> {
  if (!isActiveGameSession()) {
    cancelInFlightCaseBoot();
  }
  const code = currentLobbyCode;
  if (code && !isActiveGameSession()) {
    const res = await api(`/api/lobbies/${encodeURIComponent(code)}/leave`, {
      method: "POST",
    }).catch(() => ({ ok: false as const, status: 0, data: null }));
    if (!res.ok && res.status !== 404 && res.status !== 403) {
      /* already gone */
    }
  }
  teardownLobbySocket();
  setConnectionBanner(false);
  currentLobbyCode = "";
  preGame?.resetLobbyState();
}

function ensureConnectionLifecycleWired(): void {
  if (lifecycleWired) return;
  lifecycleWired = true;
  wireConnectionLifecycle({
    getSessionId: () => sessionId,
    getSocket: () => currentSocket,
    onHeal: () => {
      if (!isActiveGameSession()) return;
      game?.loop.wake();
      if (sessionId && !caseResolved) recomputeSessionPollMode();
      scheduleHealGameConnection();
    },
    onPageHide: () => {
      if (!isActiveGameSession() && currentLobbyCode) beaconAbandonLobby();
    },
    onBackground: () => {
      if (!isActiveGameSession()) return;
      setConnectionBanner(true, "Connection paused — reconnecting when you return…");
      if (!caseResolved) recomputeSessionPollMode();
      game?.loop.sleep();
    },
    onOffline: () => {
      if (!isActiveGameSession()) return;
      setConnectionBanner(true, "You are offline — reconnecting when the network returns…");
    },
  });
}

function dismissResumePrompt(): void {
  document.getElementById("resume-case-prompt")?.remove();
}

function showResumePrompt(opts: {
  title: string;
  body: string;
  primaryLabel: string;
  onPrimary: () => void;
  secondaryLabel?: string;
  onSecondary?: () => void;
}): void {
  dismissResumePrompt();
  const wrap = document.createElement("div");
  wrap.id = "resume-case-prompt";
  wrap.style.cssText =
    "position:fixed;inset:0;z-index:70;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,0.55);padding:16px;box-sizing:border-box;";
  const card = document.createElement("div");
  card.style.cssText =
    "max-width:420px;width:100%;padding:20px;border-radius:10px;background:#1a2030;border:1px solid #4a5568;color:#e8ecf4;";
  const h = document.createElement("h3");
  h.textContent = opts.title;
  h.style.margin = "0 0 10px";
  const p = document.createElement("p");
  p.textContent = opts.body;
  p.style.margin = "0 0 16px";
  p.style.lineHeight = "1.45";
  const row = document.createElement("div");
  row.style.display = "flex";
  row.style.gap = "10px";
  row.style.justifyContent = "flex-end";
  const secondary = document.createElement("button");
  secondary.type = "button";
  secondary.className = "secondary";
  secondary.textContent = opts.secondaryLabel ?? "Not now";
  secondary.onclick = () => {
    dismissResumePrompt();
    opts.onSecondary?.();
  };
  const primary = document.createElement("button");
  primary.type = "button";
  primary.className = "primary";
  primary.textContent = opts.primaryLabel;
  primary.onclick = () => {
    dismissResumePrompt();
    opts.onPrimary();
  };
  row.append(secondary, primary);
  card.append(h, p, row);
  wrap.append(card);
  wrap.setAttribute("role", "dialog");
  wrap.setAttribute("aria-modal", "true");
  wrap.setAttribute("aria-labelledby", "resume-case-prompt-title");
  h.id = "resume-case-prompt-title";
  card.addEventListener("click", (e) => e.stopPropagation());
  wrap.addEventListener("click", () => {
    dismissResumePrompt();
    opts.onSecondary?.();
  });
  wrap.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      dismissResumePrompt();
      opts.onSecondary?.();
    }
  });
  document.body.append(wrap);
  primary.focus();
}

async function offerResumePromptIfNeeded(): Promise<void> {
  const hint = readRejoinHint();
  if (!hint || !getToken()) return;

  if (
    sessionId === hint.sessionId &&
    game?.scene.isActive("GameScene") &&
    !el("game-host").classList.contains("hidden")
  ) {
    return;
  }

  const res = await api<SessionPayload>(`/api/sessions/${encodeURIComponent(hint.sessionId)}`);
  if (!res.ok) {
    if (res.status === 404 || res.status === 403) {
      clearRejoinHint();
      blockLobbySessionStart(hint.sessionId);
      ensurePreGame().setMessage(SESSION_EXPIRED_MSG);
    }
    return;
  }

  const data = res.data as SessionPayload;
  if (data.status === "resolved") {
    if (!data.result) {
      clearRejoinHint();
      return;
    }
    showResumePrompt({
      title: "Case closed",
      body: "Your last case has ended. View the outcome or start a new game.",
      primaryLabel: "View outcome",
      onPrimary: () => {
        displayOutcomePanel(data.result!);
      },
      secondaryLabel: "Dismiss",
      onSecondary: () => clearRejoinHint(),
    });
    return;
  }

  showResumePrompt({
    title: "Resume investigation?",
    body: "You have a case in progress. Pick up where you left off, or stay in the lobby.",
    primaryLabel: "Resume",
    onPrimary: () => {
      currentLobbyCode = hint.lobbyCode;
      ensureGameSocket(hint.lobbyCode);
      void tryStartFromLobbyAfterRoulette(hint.sessionId);
    },
    secondaryLabel: "Not now",
  });
}

async function recoverSessionOutcome(): Promise<void> {
  if (!sessionId) return;
  const s = await fetchSessionSync(sessionId);
  if (s.ok && s.data.result) {
    showOutcome(s.data.result);
  }
}

/** Stop movement and world interactions when the case is closed. */
function lockInvestigation(): void {
  investigationLocked = true;
  game?.events.emit("ccc_case_frozen");
  game?.events.emit("ccc_case_resolved", true);
  game?.events.emit("ccc_force_close_npc_dialog");
  closeSessionOverlays();
}

function closeSessionOverlays(): void {
  clearMapBlipRefreshTimer();
  closeNarrativeModal();
  closeZoneTransitConfirm();
  closeAccusationOverlays();
  const journal = document.getElementById("journal-overlay");
  const manual = document.getElementById("player-manual-overlay");
  releaseDomOverlayFocus(journal);
  releaseDomOverlayFocus(manual);
  journal?.classList.remove("open");
  manual?.classList.remove("open");
  manual?.setAttribute("aria-hidden", "true");
  resetPlayerInputOverlays();
  setRadioOpen(false);
  focusPlayfieldCanvas();
  ensureGlobalCaptureEnabled();
  selectedMapPoiId = null;
  mapTabViewZone = "downtown";
  mapBuildingsCollapsed = false;
  mapNpcsCollapsed = false;
  game?.events.emit("ccc_journal_open", false);
  game?.events.emit("ccc_map_waypoint", null);
}

function stopSessionPoll(): void {
  sessionPollController?.stop();
}

function ensureSessionPollController(): void {
  if (sessionPollController) return;
  sessionPollController = createSessionPollController(
    () => sessionId,
    {
      isCancelled: () => caseResolved || !sessionId,
      onExpired: () => handleSessionExpired(),
      onSync: (data) => {
        applySessionSyncFromServer(data);
        recomputeSessionPollMode();
      },
    }
  );
}

function startSessionPoll(): void {
  if (!sessionId || caseResolved) return;
  ensureSessionPollController();
  recomputeSessionPollMode();
}

function invalidateSessionPrefetch(): void {
  prefetchedSession = null;
  prefetchInflight = null;
}

function prefetchSession(sid: string): void {
  if (!sid || prefetchedSession?.sid === sid) return;
  if (prefetchInflight) return;
  prefetchInflight = (async (): Promise<SessionPayload | null> => {
    const s = await api<SessionPayload>(`/api/sessions/${encodeURIComponent(sid)}`);
    if (!s.ok) return null;
    const data = s.data as SessionPayload;
    prefetchedSession = { sid, data };
    return data;
  })().finally(() => {
    prefetchInflight = null;
  });
}

async function fetchSessionForStart(sid: string): Promise<{
  ok: true;
  data: SessionPayload;
} | { ok: false; data: unknown }> {
  if (prefetchedSession?.sid === sid) {
    const data = prefetchedSession.data;
    prefetchedSession = null;
    return { ok: true, data };
  }
  if (prefetchInflight) {
    const waited = await prefetchInflight;
    if (waited && prefetchedSession?.sid === sid) {
      const data = prefetchedSession.data;
      prefetchedSession = null;
      return { ok: true, data };
    }
  }
  const s = await api<SessionPayload>(`/api/sessions/${encodeURIComponent(sid)}`);
  if (!s.ok) return { ok: false, data: s.data };
  return { ok: true, data: s.data as SessionPayload };
}

function syncPhaserGameHostSize(): void {
  if (!game) return;
  applyPhaserVisualViewportSize(readGameHostLayoutRect(el("game-host"), window));
}

function applyPhaserVisualViewportSize(rect: { width: number; height: number }): void {
  if (!game) return;
  const w = Math.max(320, rect.width);
  const h = Math.max(240, rect.height);
  if (game.scale.width !== w || game.scale.height !== h) {
    game.scale.resize(w, h);
  }
}

function ensurePhaserGame(): Phaser.Game {
  if (game) return game;
  if (el("game-host").classList.contains("hidden")) {
    revealGameHostShell();
  }
  game = new Phaser.Game({
    type: Phaser.AUTO,
    parent: "game-host",
    width: window.innerWidth,
    height: window.innerHeight,
    backgroundColor: "#050508",
    physics: { default: "arcade", arcade: { gravity: { x: 0, y: 0 } } },
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.NO_CENTER,
    },
    scene: [],
  });
  wirePhaserKeyboardForDomTyping(() => game?.input.keyboard ?? undefined);
  setPlayerControlsRestoredListener(() => {
    hideAllDeferredLoading();
    game?.events.emit("ccc_player_controls_restored");
  });
  syncPhaserGameHostSize();
  return game;
}

function destroyPhaserGame(): void {
  if (!game) return;
  try {
    game.destroy(true);
  } catch {
    /* canvas may already be torn down */
  }
  game = null;
  phaserShellWired = false;
}

function stopGameScenes(): void {
  if (!game) return;
  dismissNpcConversationUi();
  closeNarrativeModal();
  setRadioOpen(false);
  if (game.scene.isActive("UIScene")) game.scene.stop("UIScene");
  if (game.scene.isActive("GameScene")) game.scene.stop("GameScene");
}

/** Avoid Phaser resizing the WebGL framebuffer to 0×0 when #game-host is hidden. */
function parkPhaserRenderer(): void {
  if (!game) return;
  try {
    const w = Math.max(320, game.scale.width || window.innerWidth);
    const h = Math.max(240, game.scale.height || window.innerHeight);
    if (game.scale.width !== w || game.scale.height !== h) {
      game.scale.resize(w, h);
    }
  } catch {
    // Parent may already be display:none; still stop the render loop.
  }
  game.loop.sleep();
}

function hideGameHost(): void {
  unbindVisualViewport?.();
  unbindVisualViewport = null;
  unmountInGameOverlaysFromShell();
  el("app-shell").classList.remove("ccc-in-game");
  parkPhaserRenderer();
  el("game-host").classList.add("hidden");
}

function revealGameHostShell(): void {
  const shell = el("app-shell");
  const host = el("game-host");
  host.classList.remove("hidden");
  mountInGameOverlaysToShell(shell);

  unbindVisualViewport?.();
  unbindVisualViewport = bindVisualViewportSync({
    shell,
    gameHost: host,
    onResize: (rect) => applyPhaserVisualViewportSize(rect),
  });
  syncPhaserGameHostSize();
}

function showGameHost(): void {
  revealGameHostShell();
  game?.loop.wake();
}

/** Stop live scenes and drop case art before the next GameScene boot (sprites must not render purged keys). */
function prepareCaseSceneBoot(g: Phaser.Game): void {
  stopGameScenes();
  g.loop.sleep();
  purgeCaseTextures(g);
}

function ensureGameScenesRegistered(g: Phaser.Game): void {
  if (!g.scene.getScene("GameScene")) {
    g.scene.add("GameScene", GameScene, false);
    g.scene.add("UIScene", UIScene, false);
  }
}

function restartGameScenes(opts?: { skipPrepare?: boolean }): void {
  const g = ensurePhaserGame();
  ensureGameScenesRegistered(g);
  if (!opts?.skipPrepare) prepareCaseSceneBoot(g);
  syncPhaserGameHostSize();
  g.loop.wake();
  g.scene.start("GameScene");
}

async function resetGameToLobby(message?: string): Promise<void> {
  await scheduleCaseRuntimeReset(async () => {
    await performCaseRuntimeReset({ leaveLobby: true, hideGameHost: true });
    el("lobby-panel").classList.remove("hidden");
    await showSignedInHub(message);
  });
}

type ReturnToAppHomeOpts = {
  postCaseOutcome?: "win" | "loss";
  lobbyMessage?: string;
};

/** Leave the finished case and return to the app entry screen (auth or new-game lobby). */
async function returnToAppHome(opts?: string | ReturnToAppHomeOpts): Promise<void> {
  const resolved: ReturnToAppHomeOpts =
    typeof opts === "string" ? { lobbyMessage: opts } : (opts ?? {});
  hideOutcome();
  closeSessionOverlays();
  await performCaseRuntimeReset({ leaveLobby: true, hideGameHost: true, clearSession: true });

  investigationElapsedSec = 0;
  journalUiActiveTab = -1;
  lastNotesAgeRefreshMinute = -1;
  lastAccuseResult = null;
  journal = emptyJournal();
  lastKnownClueIds = new Set();
  clearRejoinHint();

  const journalReset = document.getElementById("journal-overlay");
  releaseDomOverlayFocus(journalReset);
  journalReset?.classList.remove("open");
  setPlayerOverlayOpen("journal", false);
  el("lobby-panel").classList.add("hidden");
  el("auth-panel").classList.add("hidden");

  if (resolved.postCaseOutcome) {
    setPostCaseBanner(resolved.postCaseOutcome === "win");
  }

  if (getToken()) {
    await showSignedInHub(resolved.lobbyMessage);
  } else {
    showLanding(resolved.lobbyMessage);
  }
}

function dismissOutcome(): void {
  const won = lastAccuseResult?.correct === true;
  hideOutcome();
  void returnToAppHome({ postCaseOutcome: won ? "win" : "loss" });
}

function ensureGameSocket(lobbyCode?: string): void {
  const code = lobbyCode ?? currentLobbyCode ?? readRejoinHint()?.lobbyCode ?? "";
  if (!code) return;
  if (!currentSocket?.connected || currentLobbyCode !== code) {
    connectLobbySocket(code);
    return;
  }
  wireGameSessionEndedListener();
  wireGameJoinedHandler();
  if (sessionId && !startingSessionId) scheduleHealGameConnection();
}

function markCaseResolved(): void {
  if (caseResolved) return;
  caseResolved = true;
  clearRejoinHint();
  stopSessionPoll();
  lockInvestigation();
}

let openJournalAtTab:
  | ((tab: number, opts?: { briefFooter?: boolean }) => boolean)
  | null = null;
let ensureFirstCaseBriefOpened: (() => void) | null = null;

function displayOutcomePanel(result: AccuseResult): void {
  lastAccuseResult = result;
  const overlay = el("outcome-overlay");
  const header = overlay.querySelector("#outcome-header")!;
  const title = el("outcome-title");
  const body = el("outcome-body");
  header.classList.remove("win", "loss");
  header.classList.add(result.correct ? "win" : "loss");
  title.textContent = result.correct
    ? "The judge signed the warrant, and the killer was arrested!"
    : "Warrant denied, and the killer fled town!";
  body.innerHTML = `<p><strong>Investigation time:</strong> ${escapeHtml(formatElapsedClock(investigationElapsedSec))}</p>
<p>${escapeHtml(result.solution_summary)}</p>`;
  const closeBtn = el("outcome-close");
  closeBtn.textContent = "Back to lobby";
  overlay.classList.add("open");
  overlay.setAttribute("aria-hidden", "false");
  setPlayerOverlayOpen("outcome", true);
}

function showOutcome(result: AccuseResult): void {
  closeAccusationOverlays();
  const overlay = el("outcome-overlay");
  const alreadyOpen = overlay.classList.contains("open");
  markCaseResolved();
  if (alreadyOpen) return;
  displayOutcomePanel(result);
}

function hideOutcome(): void {
  const overlay = el("outcome-overlay");
  overlay.classList.remove("open");
  overlay.setAttribute("aria-hidden", "true");
  setPlayerOverlayOpen("outcome", false);
}

function clueToastScene(): Phaser.Scene | null {
  if (!game) return null;
  const ui = game.scene.getScene("UIScene");
  if (ui?.scene.isActive()) return ui;
  return game.scene.getScene("GameScene");
}

let journalUiRefreshScheduled = false;

function applyJournalFromServer(next: Record<string, unknown>): void {
  journal = next;
  lastKnownClueIds = new Set((journal.discovered_clues as string[]) ?? []);
  game?.registry.set("coherenceJournal", asCoherenceJournal(journal));
  game?.events.emit("ccc_journal_sync");
  if (!refreshJournalUi) return;
  if (journalUiRefreshScheduled) return;
  journalUiRefreshScheduled = true;
  requestAnimationFrame(() => {
    journalUiRefreshScheduled = false;
    refreshJournalUi?.();
  });
}

function notifyBuildingClearanceGranted(
  manifest: CaseManifestV1,
  approverInstanceId: string
): void {
  const approver = manifest.npcs.find((n) => n.instance_id === approverInstanceId);
  const approverName = approver?.name?.trim() || npcDisplayName(manifest, approverInstanceId);
  const unlocked = manifest.buildings.filter(
    (b) => b.investigation?.requires_permission_from === approverInstanceId
  );
  if (!unlocked.length) return;
  const names = unlocked
    .map((b) => resolveBuildingDisplayName(manifest, b.building_id))
    .join(", ");
  showGameStatusMsg(`${approverName} cleared you to search ${names}.`, 5000);
  appendDispatchMessage(`${approverName} cleared access — ${names}.`);
}

type CaseNoteEntry = {
  id: string;
  userId: string;
  displayName: string;
  text: string;
  createdAtMs: number;
  createdAtElapsedSec?: number;
};

function caseNoteSortKey(n: CaseNoteEntry): number {
  if (typeof n.createdAtElapsedSec === "number" && Number.isFinite(n.createdAtElapsedSec)) {
    return n.createdAtElapsedSec;
  }
  return n.createdAtMs / 1000;
}

/** Update note timestamps in-place so the compose box is not destroyed. */
function refreshNoteAgesInFeed(): void {
  const feed = document.querySelector("#journal-body .case-notes-feed");
  if (!feed) return;
  const byId = new Map(
    ((journal.case_notes as CaseNoteEntry[]) ?? []).map((n) => [n.id, n])
  );
  feed.querySelectorAll<HTMLElement>(".case-note[data-note-id]").forEach((el) => {
    const id = el.dataset.noteId;
    const note = id ? byId.get(id) : undefined;
    if (!note) return;
    const meta = el.querySelector(".case-note-meta");
    if (!meta) return;
    meta.innerHTML = `<strong>${escapeHtml(note.displayName)}</strong> · ${escapeHtml(formatCaseNoteAge(note, investigationElapsedSec))}`;
  });
}

function emptyJournal(): Record<string, unknown> {
  return {
    discovered_clues: [] as string[],
    talked_to_npcs: [] as string[],
    visited_buildings: [] as string[],
    discovered_npcs: [] as string[],
    discovered_building_ids: [] as string[],
    flagged_suspects: [] as { npc_instance_id: string; reason: string }[],
    pinned_clue_ids: [] as string[],
    triangulation_confirmed: [] as string[],
    case_notes: [] as CaseNoteEntry[],
    ruled_out_npc_ids: [] as string[],
  };
}

let journal: Record<string, unknown> = emptyJournal();

function el(id: string): HTMLElement {
  return document.getElementById(id)!;
}

function ensurePreGame(): PreGameController {
  if (!preGame) initPreGameApp();
  return preGame!;
}

function initPreGameApp(): void {
  preGame = createPreGameController({
    formatApiError,
    hideGameHost,
    hideSessionLoading,
    dismissAllGameLoading,
    clearPostCaseBanner,
    consumePostCaseOutcome,
    connectLobbySocket,
    teardownLobbySocket,
    setConnectionBanner,
    ensureGameSocket,
    tryStartFromLobbyAfterRoulette,
    fetchLobbySessionReveal,
    prefetchSession,
    checkSessionAvailable,
    abandonStaleLobbySession,
    clearLobbySessionStartBlocks,
    leaveHubSubflow,
    abandonCurrentLobby,
    readRejoinHint,
    clearRejoinHint,
    getUserId: () => userId,
    setUserId: (id) => {
      userId = id;
    },
    onEnterGame: () => {
      sessionDomAbort?.abort();
      sessionDomAbort = null;
      closeSessionOverlays();
    },
  });
  preGame.setSendLobbyChat((code, text) => {
    currentSocket?.emit("lobby:chat", { code, text });
  });
  refreshLobbyView = (code) => {
    void preGame?.getRefreshLobbyView()?.(code);
  };
}

function showLanding(message?: string): void {
  dismissAllGameLoading();
  ensurePreGame().renderLanding();
  if (message) ensurePreGame().setMessage(message);
}

async function showSignedInHub(message?: string): Promise<void> {
  if (!isActiveGameSession()) {
    dismissAllGameLoading();
  }
  sessionDomAbort?.abort();
  sessionDomAbort = null;
  closeSessionOverlays();
  const pg = ensurePreGame();
  const profile = await pg.fetchMe();
  if (!profile || !getToken()) {
    if (getToken()) setToken(null);
    showLanding("Session expired — sign in again.");
    return;
  }
  await pg.showSignedInHub();
  if (message) pg.setMessage(message);
}

function syncLiveSocketToPhaser(): void {
  if (!game) return;
  const ccc = game.registry.get("ccc") as { socket?: Socket | null } | undefined;
  if (!ccc) return;
  ccc.socket = currentSocket;
  setLeaderFollowMirrorSession(sessionId, currentSocket, userId);
  rebindPlayerFollowSocket(currentSocket);
  game.registry.events.emit("ccc_rebind_socket");
}

function connectLobbySocket(code: string): void {
  const t = getToken();
  if (!t) return;
  ensureConnectionLifecycleWired();
  currentLobbyCode = code;
  if (!isActiveGameSession()) clearStaleGameSessionState();
  if (currentSocket) teardownLobbySocket();
  currentSocket = io(gameSocketOptions(t));
  wireGameSessionEndedListener();
  wireGameJoinedHandler();
  currentSocket.off("connect");
  currentSocket.on("connect", () => {
    setConnectionBanner(false);
    syncLiveSocketToPhaser();
    if (currentLobbyCode) currentSocket?.emit("lobby:join", currentLobbyCode);
    if (isActiveGameSession() && !startingSessionId) scheduleHealGameConnection();
  });
  currentSocket.off("disconnect");
  currentSocket.on("disconnect", () => {
    if (!isActiveGameSession()) {
      setConnectionBanner(false);
      return;
    }
    if (document.visibilityState === "hidden") {
      setConnectionBanner(true, "Connection paused — reconnecting when you return…");
    } else {
      setConnectionBanner(true, "Connection lost — reconnecting…");
    }
  });
  currentSocket.off("connect_error");
  currentSocket.on("connect_error", () => {
    if (isActiveGameSession()) {
      setConnectionBanner(true, "Connection lost — reconnecting…");
    }
  });
  currentSocket.off("lobby:updated");
  currentSocket.on(
    "lobby:updated",
    (lobby: {
      code?: string;
      sessionId?: string | null;
      status?: string;
      members?: { userId: string }[];
    }) => {
      const sid = lobby.sessionId;
      if (
        sid &&
        lobby.status === "closed" &&
        lobby.members?.some((m) => m.userId === userId)
      ) {
        void (async () => {
          if (isLobbySessionStartBlocked(sid)) return;
          const available = await checkSessionAvailable(sid);
          if (!available) {
            const code = lobby.code ?? currentLobbyCode;
            if (code) await abandonStaleLobbySession(code);
            return;
          }
          const lobbyCode = lobby.code ?? currentLobbyCode;
          void (async () => {
            const reveal = lobbyCode
              ? await fetchLobbySessionReveal(lobbyCode)
              : null;
            await tryStartFromLobbyAfterRoulette(sid, reveal);
          })();
        })();
        return;
      }
      const lobbyCode = lobby.code ?? currentLobbyCode;
      if (lobbyCode) void refreshLobbyView?.(lobbyCode);
    }
  );
  currentSocket.off("lobby:chat");
  currentSocket.on(
    "lobby:chat",
    (m: { displayName?: string; username?: string; text: string }) => {
      const label = m.displayName?.trim() || m.username?.trim() || "Detective";
      ensurePreGame().appendLobbyChatMessage(label, m.text);
    }
  );
  currentSocket.emit("lobby:join", code);
  rebindRadioSocket();
}

function onRadioMessage(m: { displayName?: string; username?: string; text: string }): void {
  const label = m.displayName?.trim() || m.username?.trim() || "Detective";
  onAccusationRadioMessage(label, m.text);
  onGameRadioMessage(m);
}

function rebindRadioSocket(): void {
  if (!currentSocket) return;
  currentSocket.off("game:radio");
  currentSocket.on("game:radio", onRadioMessage);
}

function setupRadioOverlay(): void {
  if (!game) return;
  wireRadioOverlay(game, {
    setPlayerOverlayOpen,
    isAccusationPhaseActive,
    accusationBlockedMessage,
    blockIfFollowingLeader,
    isFollowingLeader,
    leaderHasFollowers,
    emitLeaderMirror,
    getManualOpen: () =>
      document.getElementById("player-manual-overlay")?.classList.contains("open") === true,
    dismissNpcConversationUi,
    focusPlayfieldCanvas,
    ensureGlobalCaptureEnabled,
    showGameStatusMsg,
    getSessionId: () => sessionId,
    caseResolved: () => caseResolved,
    investigationLocked: () => investigationLocked,
    emitRadio: (text) => {
      if (!sessionId) return;
      currentSocket?.emit("game:radio", { sessionId, text });
    },
    syncVisualViewportBounds,
  });
  rebindRadioSocket();
}

type CccRegistry = {
  caseManifest: CaseManifestV1;
  playerCount: number;
  playerNames?: string[];
  bootMeta?: { alreadyResolved: boolean; result: AccuseResult | null };
  userId?: string;
};

function wirePhaserShellOnce(): void {
  if (phaserShellWired || !game) return;
  phaserShellWired = true;
  const g = game;
  g.registry.events.on("journal_patch", (patch: Record<string, unknown>) => {
    if (caseResolved || investigationLocked) return;
    void patchJournal(patch);
  });
  g.events.on("toggle_radio", () => {
    if (isNpcConversationOverlayOpen() && !isRadioOverlayOpen()) dismissNpcConversationUi();
    toggleRadioFromHud();
  });
  g.events.on("ccc_open_journal_tab", (tab: number) => {
    if (typeof tab !== "number" || !Number.isFinite(tab)) return;
    if (blockIfFollowingLeader()) return;
    openJournalAtTab?.(tab);
  });
  g.registry.events.on("zone_transit_loading", (zone: "downtown" | "maple_heights") => {
    showSessionLoading(zone);
  });
  g.registry.events.on("zone_transit_done", () => {
    hideSessionLoading();
  });
  g.registry.events.on("zone_transit_failed", () => {
    hideSessionLoading();
    showGameStatusMsg("Could not change neighborhood — try again.", 4000);
  });
  g.registry.events.on("zone_changed", (zone: "downtown" | "maple_heights") => {
    currentGameZone = zone;
    const code = currentLobbyCode || readRejoinHint()?.lobbyCode;
    if (code && sessionId) saveRejoinHint(code, sessionId, zone);
    g.events.emit("ccc_map_zone_changed", zone);
  });
  g.registry.events.on("ccc_timer", (elapsedSec: number) => {
    if (typeof elapsedSec !== "number" || !Number.isFinite(elapsedSec)) return;
    investigationElapsedSec = Math.max(0, Math.floor(elapsedSec));
    setRadioElapsedSec(investigationElapsedSec);
    onInvestigationTimerTick(investigationElapsedSec, clueToastScene(), {
      caseResolved,
      accusationPhase: isAccusationPhaseActive(),
    });
    const journalEl = document.getElementById("journal-overlay");
    if (
      journalUiActiveTab !== JOURNAL_NOTES_TAB_INDEX ||
      !journalEl?.classList.contains("open")
    ) {
      return;
    }
    const minute = Math.floor(investigationElapsedSec / 60);
    if (minute === lastNotesAgeRefreshMinute) return;
    lastNotesAgeRefreshMinute = minute;
    refreshNoteAgesInFeed();
  });
  g.registry.events.on("open_dialog", (npcInstanceId: string) => {
    if (!game || caseResolved || investigationLocked || isAccusationPhaseActive()) return;
    const ccc = game.registry.get("ccc") as CccRegistry | undefined;
    const manifest = ccc?.caseManifest;
    if (!manifest) return;
    const npc = manifest.npcs.find((n) => n.instance_id === npcInstanceId);
    if (!npc) return;
    const names = ccc.playerNames ?? [];
    let dialogLoader: LoadingHandle | null = null;
    const loaderTimer = setTimeout(() => {
      dialogLoader = showDeferredLoading(currentGameZone);
    }, 50);
    const clearLoader = (): void => {
      clearTimeout(loaderTimer);
      hideDeferredLoading(dialogLoader);
      dialogLoader = null;
    };
    game.events.once("ccc_npc_case_notes_shown", clearLoader);
    game.events.once("ccc_npc_dialog_shown", clearLoader);
    game.events.once("ccc_npc_case_notes_closed", clearLoader);
    game.events.once("ccc_npc_dialog_closed", clearLoader);
    game.events.emit("ccc_open_npc_case_notes", {
      npc,
      manifest,
      getJournal: () => asCoherenceJournal(journal),
      playerCount: ccc.playerCount,
      names,
      victimName: manifest.victim.name,
    });
  });
  g.events.on("ccc_npc_case_notes_shown", (payload: { instanceId?: string }) => {
    const id = payload?.instanceId;
    if (!id || caseResolved || investigationLocked) return;
    const dn = new Set((journal.discovered_npcs as string[]) ?? []);
    const talked = new Set((journal.talked_to_npcs as string[]) ?? []);
    const patch: Record<string, unknown> = {};
    if (!dn.has(id)) {
      dn.add(id);
      patch.discovered_npcs = [...dn];
    }
    if (!talked.has(id)) {
      talked.add(id);
      patch.talked_to_npcs = [...talked];
    }
    if (Object.keys(patch).length > 0) void patchJournal(patch);
  });
  g.events.on("ccc_npc_dialog_closed", (result: NpcDialogCloseResult) => {
    if (!result.progressed || investigationLocked) return;
    const ccc = game?.registry.get("ccc") as CccRegistry | undefined;
    const manifest = ccc?.caseManifest;
    if (!manifest) return;
    const prevClues = new Set((journal.discovered_clues as string[]) ?? []);
    const beforeClearance = userBuildingClearanceSet(asCoherenceJournal(journal), userId);
    const talked = new Set((journal.talked_to_npcs as string[]) ?? []);
    talked.add(result.talkedInstanceId);
    const dn = new Set((journal.discovered_npcs as string[]) ?? []);
    dn.add(result.talkedInstanceId);
    const dc = new Set((journal.discovered_clues as string[]) ?? []);
    for (const cid of result.clueIdsToReveal) dc.add(cid);
    const clearancePatch = result.grantedBuildingClearance
      ? buildingClearanceJournalPatch(userId, result.talkedInstanceId)
      : {};
    void (async () => {
      const ok = await patchJournal({
        talked_to_npcs: [...talked],
        discovered_npcs: [...dn],
        discovered_clues: [...dc],
        ...clearancePatch,
      });
      if (!ok) return;
      lastKnownClueIds = new Set((journal.discovered_clues as string[]) ?? []);
      if (result.grantedBuildingClearance) {
        const afterClearance = userBuildingClearanceSet(asCoherenceJournal(journal), userId);
        if (
          !beforeClearance.has(result.talkedInstanceId) &&
          afterClearance.has(result.talkedInstanceId)
        ) {
          notifyBuildingClearanceGranted(manifest, result.talkedInstanceId);
        }
      }
    })();
  });
  const applyBuildingInvestigationJournal = (
    bid: string,
    res: ReturnType<typeof resolveBuildingInvestigation>,
    decor: boolean
  ): void => {
    if (!res.permitted || investigationLocked || decor) return;
    const prevClues = new Set((journal.discovered_clues as string[]) ?? []);
    const vis = new Set((journal.visited_buildings as string[]) ?? []);
    vis.add(bid);
    const discoveredB = new Set((journal.discovered_building_ids as string[]) ?? []);
    discoveredB.add(bid);
    const clues = new Set((journal.discovered_clues as string[]) ?? []);
    for (const c of res.clueIdsToReveal) clues.add(c);
    const newClueIds = res.clueIdsToReveal.filter((cid) => !prevClues.has(cid));
    void patchJournal({
      visited_buildings: [...vis],
      discovered_building_ids: [...discoveredB],
      discovered_clues: [...clues],
    });
    lastKnownClueIds = new Set([...prevClues, ...newClueIds]);
  };

  const showBuildingInvestigationModal = (
    bid: string,
    opts: {
      decor: boolean;
      manifest: CaseManifestV1;
      readOnly?: boolean;
      surface: BuildingInvestigationSurface;
    }
  ): void => {
    if (!game) return;
    if (isFollowingLeader() && !opts.readOnly) return;
    const b = opts.manifest.buildings.find((x) => x.building_id === bid) ?? null;
    let buildingLoader: LoadingHandle | null = null;
    const loaderTimer = setTimeout(() => {
      buildingLoader = showDeferredLoading(currentGameZone);
    }, 50);
    const clearLoader = (): void => {
      clearTimeout(loaderTimer);
      hideDeferredLoading(buildingLoader);
      buildingLoader = null;
    };
    game.events.once("ccc_narrative_modal_shown", clearLoader);
    game.events.once("ccc_narrative_modal_closed", clearLoader);
    try {
      const ccc = game.registry.get("ccc") as CccRegistry | undefined;
      const invOpts = {
        decor: opts.decor,
        manifest: opts.manifest,
        ...buildingInvestigationOpts(ccc),
        surface: opts.surface,
      };
      const res = resolveBuildingInvestigation(b, asCoherenceJournal(journal), invOpts);
      const title = resolveBuildingDisplayName(opts.manifest, bid, { decor: opts.decor });
      const sceneLead = b && !opts.decor ? buildingLeadDescription(b) : null;
      const leadParts: string[] = [];
      if (!res.permitted && res.denialLead?.trim()) leadParts.push(res.denialLead.trim());
      if (sceneLead) leadParts.push(sceneLead);
      openNarrativeModal({
        title,
        leadText: leadParts.length > 0 ? leadParts.join("\n\n") : undefined,
        bodyText: res.narrative,
        buildingId: bid,
        manifest: opts.manifest,
        decor: opts.decor,
        readOnly: opts.readOnly,
        phaserGame: game,
        onClose: () => {
          clearLoader();
          if (!opts.readOnly && opts.surface === "interior_search") {
            applyBuildingInvestigationJournal(bid, res, opts.decor);
          }
        },
      });
    } finally {
      clearLoader();
    }
  };

  const enterBuildingInteriorFromWorld = (bid: string): void => {
    if (!game) return;
    const ccc = game.registry.get("ccc") as CccRegistry | undefined;
    const manifest = ccc?.caseManifest;
    const layerA = ccc?.layerA;
    if (!manifest || !layerA) return;
    const placement = layerA.building_placements?.find((p) => p.id === bid);
    const decor = placement?.decor === true;
    const gs = game.scene.getScene("GameScene") as GameScene | undefined;
    if (!gs?.scene.isActive()) return;
    const title = resolveBuildingDisplayName(manifest, bid, { decor });
    const plan = buildInteriorContentPlan(
      layerA.seedId,
      manifest,
      bid,
      asCoherenceJournal(journal),
      decor,
      buildingInvestigationOpts(ccc)
    );
    gs.enterBuildingInterior(bid, plan, title);
    if (!decor) {
      const vis = new Set((journal.visited_buildings as string[]) ?? []);
      const discoveredB = new Set((journal.discovered_building_ids as string[]) ?? []);
      if (!vis.has(bid) || !discoveredB.has(bid)) {
        vis.add(bid);
        discoveredB.add(bid);
        void patchJournal({
          visited_buildings: [...vis],
          discovered_building_ids: [...discoveredB],
        });
      }
    }
  };

  g.registry.events.on(
    "open_building",
    (bid: string, opts?: { enterInterior?: boolean }) => {
      if (!game || caseResolved || investigationLocked || isAccusationPhaseActive()) return;
      if (isFollowingLeader()) return;
      const ccc = game.registry.get("ccc") as CccRegistry | undefined;
      const manifest = ccc?.caseManifest;
      const layerA = ccc?.layerA;
      if (!manifest || !layerA) return;
      const placement = layerA.building_placements?.find((p) => p.id === bid);
      const decor = placement?.decor === true;
      const b = manifest.buildings.find((x) => x.building_id === bid) ?? null;
      if (!b && !decor) return;
      const invOpts = { decor, manifest, ...buildingInvestigationOpts(ccc) };
      const res = resolveBuildingInvestigation(b, asCoherenceJournal(journal), invOpts);
      if (!res.permitted) {
        showBuildingInvestigationModal(bid, { decor, manifest, surface: "exterior" });
        return;
      }
      enterBuildingInteriorFromWorld(bid);
    }
  );

  g.registry.events.on("interior_clue_interact", (bid: string | null) => {
    if (!bid || !game || caseResolved || investigationLocked) return;
    if (blockIfFollowingLeader("Stop following — your partner leads this search.")) return;
    const ccc = game.registry.get("ccc") as CccRegistry | undefined;
    const manifest = ccc?.caseManifest;
    const layerA = ccc?.layerA;
    if (!manifest || !layerA) return;
    const placement = layerA.building_placements?.find((p) => p.id === bid);
    const decor = placement?.decor === true;
    showBuildingInvestigationModal(bid, { decor, manifest, surface: "interior_search" });
  });
}

function leaveHubSubflow(): void {
  if (!isActiveGameSession()) {
    void abandonCurrentLobby();
    clearStaleGameSessionState();
    return;
  }
  teardownLobbySocket();
  setConnectionBanner(false);
}

function shouldSkipCaseRoulette(sid: string): boolean {
  const hint = readRejoinHint();
  return hint?.sessionId === sid;
}

async function fetchLobbySessionReveal(
  lobbyCode: string
): Promise<CaseStartReveal | null> {
  const res = await api<{
    sessionId: string;
    chosenTitle: string;
    catalog: { total: number; poolTotal?: number; titles: string[] };
  }>(`/api/lobbies/${encodeURIComponent(lobbyCode)}/session`);
  if (!res.ok) return null;
  const data = res.data as {
    chosenTitle?: string;
    catalog?: { total: number; poolTotal?: number; titles: string[] };
  };
  if (!data?.chosenTitle || !data.catalog) return null;
  return { chosenTitle: data.chosenTitle, catalog: data.catalog };
}

async function tryStartFromLobbyAfterRoulette(
  sid: string,
  reveal?: CaseStartReveal | null
): Promise<void> {
  if (isLobbySessionStartBlocked(sid)) return;
  if (shouldSkipCaseRoulette(sid)) {
    await tryStartFromLobby(sid);
    return;
  }
  if (caseRouletteSessionId && caseRouletteSessionId !== sid) {
    closeCaseRoulette();
  }
  if (caseRouletteSessionId === sid) return;

  caseRouletteSessionId = sid;
  prefetchSession(sid);

  let resolved = reveal ?? null;
  if (!resolved && currentLobbyCode) {
    resolved = await fetchLobbySessionReveal(currentLobbyCode);
  }

  if (!resolved) {
    caseRouletteSessionId = "";
    await tryStartFromLobby(sid);
    return;
  }

  try {
    await runCaseRoulette({
      total: resolved.catalog.total,
      poolTotal: resolved.catalog.poolTotal,
      titles: resolved.catalog.titles,
      chosenTitle: resolved.chosenTitle,
    });
  } catch (err) {
    if (err instanceof RouletteAbortedError) return;
    throw err;
  } finally {
    if (caseRouletteSessionId === sid) caseRouletteSessionId = "";
  }

  if (isLobbySessionStartBlocked(sid)) return;
  await tryStartFromLobby(sid);
}

async function tryStartFromLobby(sid: string): Promise<void> {
  if (isLobbySessionStartBlocked(sid)) return;
  if (sessionId === sid && game?.scene.isActive("GameScene")) {
    scheduleHealGameConnection();
    return;
  }
  if (startingSessionId && startingSessionId !== sid) {
    await scheduleCaseRuntimeReset(async () => {
      await performCaseRuntimeReset({ leaveLobby: false, hideGameHost: true });
    });
  }
  if (startingSessionId === sid) {
    if (!isLobbySessionStartBlocked(sid)) scheduleTryStartFromLobbyRetry(sid);
    return;
  }
  dismissResumePrompt();
  startingSessionId = sid;
  prefetchSession(sid);
  ensureGameSocket();
  try {
    await startGame(sid);
  } finally {
    if (startingSessionId === sid) startingSessionId = "";
  }
}

async function patchJournal(patch: Record<string, unknown>): Promise<boolean> {
  if (caseResolved || investigationLocked) return false;
  const prev = journal;
  const res = await api<{ journal: Record<string, unknown> }>(
    `/api/sessions/${sessionId}/journal`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ journal: patch }),
    }
  );
  if (!res.ok) {
    showGameStatusMsg(formatApiError(res.data, "Could not save case file — try again."));
    return false;
  }
  if (res.data?.journal) {
    applyJournalFromServer(res.data.journal);
  }
  return true;
}

async function startGame(sid: string): Promise<void> {
  await caseRuntimeResetChain;
  clearPostCaseBanner();
  const isResume = readRejoinHint()?.sessionId === sid;
  const resumeZoneEarly =
    isResume && readRejoinHint()?.zone === "maple_heights" ? "maple_heights" : "downtown";
  const bootGen = ++caseBootGeneration;
  clearBootWatchdog();
  showSessionLoading(resumeZoneEarly);
  hideAllDeferredLoading();
  teardownAccusationController();
  hideOutcome();
  const journalAtStart = el("journal-overlay");
  releaseDomOverlayFocus(journalAtStart);
  journalAtStart.classList.remove("open");
  setPlayerOverlayOpen("journal", false);
  if (!isResume) investigationElapsedSec = 0;
  investigationLocked = false;
  resetRadioDispatch();
  resetInvestigationNudges();
  sessionId = sid;
  const rejoinHint = readRejoinHint();
  if (currentLobbyCode || rejoinHint?.lobbyCode) {
    saveRejoinHint(currentLobbyCode || rejoinHint!.lobbyCode, sid);
  }
  ensureGameSocket(currentLobbyCode || rejoinHint?.lobbyCode);
  try {
  const s = await fetchSessionForStart(sessionId);
  if (!s.ok) {
    const err = (s.data as { error?: string })?.error;
    if (err === "not_found" || err === "forbidden") {
      blockLobbySessionStart(sid);
      dismissAllGameLoading();
      handleSessionExpired();
      return;
    }
    await resetGameToLobby(
      formatApiError(
        s.data,
        "Could not load this case session. Try leaving the lobby and rejoining."
      )
    );
    return;
  }
  clearLobbySessionStartBlocks();
  let data = s.data;
  const alreadyResolved = data.status === "resolved";
  caseResolved = alreadyResolved;
  if (alreadyResolved) investigationLocked = true;
  journal = { ...emptyJournal(), ...(data.journal ?? {}) };
  lastKnownClueIds = new Set((journal.discovered_clues as string[]) ?? []);
  if (!alreadyResolved) {
    const begin = await api<{ startedAt?: string }>(
      `/api/sessions/${sessionId}/begin`,
      { method: "POST" }
    );
    if (!begin.ok) {
      await resetGameToLobby(
        formatApiError(begin.data, "Could not start the investigation.")
      );
      return;
    }
    const beginStarted = (begin.data as { startedAt?: string })?.startedAt;
    if (beginStarted) {
      data = { ...data, startedAt: beginStarted };
    } else {
      const refetch = await api<SessionPayload>(`/api/sessions/${sessionId}`);
      if (refetch.ok) data = refetch.data as SessionPayload;
    }
  }
  el("lobby-panel").classList.add("hidden");
  revealGameHostShell();
  currentGameZone = resumeZoneEarly;
  wireGameJoinedHandler();
  currentSocket?.emit("game:join", sessionId);
  const names = playerOrderedNames(
    data.players,
    data.players.map((p) => p.displayName)
  );
  const manifest = prepareClientCaseManifest(data.caseManifest);
  manifest.title = resolveCaseDisplayTitle(manifest);
  const lobbyPlayerCount = data.playerCount;
  sessionPlayerCount = lobbyPlayerCount;
  sessionManifest = manifest;
  sessionPlayerNames = names;
  sessionPlayersList = data.players;
  const startedMs = data.startedAt ? Date.parse(data.startedAt) : NaN;
  const timerElapsedOffsetSeconds = Number.isFinite(startedMs)
    ? Math.max(0, Math.floor((Date.now() - startedMs) / 1000))
    : 0;
  investigationElapsedSec = timerElapsedOffsetSeconds;
  setRadioElapsedSec(investigationElapsedSec);
  const selfPlayer = data.players.find((p) => p.userId === userId);
  const playerCharacterNpcId =
    selfPlayer?.characterNpcId ?? data.players[0]?.characterNpcId ?? "pc_knight_m";
  const g = ensurePhaserGame();
  g.registry.set("userId", userId);
  g.registry.set("coherenceJournal", asCoherenceJournal(journal));
  g.registry.set("ccc", {
    sessionId,
    token: getToken() ?? "",
    caseManifest: manifest,
    layerA: data.layerA,
    socket: currentSocket,
    userId,
    players: data.players,
    playerName: selfPlayer?.displayName ?? names[0] ?? "Detective",
    playerCharacterNpcId,
    playerCount: lobbyPlayerCount,
    timerElapsedOffsetSeconds,
    resumeZone: isResume ? resumeZoneEarly : undefined,
    onJournalPatch: (_path: string, _value: unknown) => {},
    getJournal: () => journal,
    bootMeta: { alreadyResolved, result: data.result },
    playerNames: names,
  });
  syncLiveSocketToPhaser();
  wirePhaserShellOnce();
  setupSessionOverlays(manifest, names, g, lobbyPlayerCount);
  bindWorldReadyHideLoader(g, bootGen);
  if (currentSocket) bindAccusationController(currentSocket);
  prepareCaseSceneBoot(g);
  restartGameScenes({ skipPrepare: true });
  if (data.accusationPhase) {
    resumeAccusationPhase(data.accusationPhase);
    recomputeSessionPollMode();
    clearBootWatchdog();
  } else if (!alreadyResolved && game?.scene.isActive("GameScene")) {
    finishInvestigationBoot(g, bootGen);
  } else if (!alreadyResolved) {
    scheduleBootWatchdog(g, bootGen);
  }
  setupRadioOverlay();
  setupOutcomePanel();
  currentSocket?.off("game:journal_update");
  currentSocket?.on("game:journal_update", (p: { journal: Record<string, unknown> }) => {
    if (caseResolved || !p?.journal) return;
    applyJournalFromServer(p.journal);
  });
  currentSocket?.off("game:clue_discovered");
  currentSocket?.on(
    "game:clue_discovered",
    (p: { clueId: string; discovererDisplayName: string }) => {
      if (caseResolved || !p?.clueId) return;
      const m = (game?.registry.get("ccc") as { caseManifest?: CaseManifestV1 })?.caseManifest;
      const scene = clueToastScene();
      if (!m || !scene) return;
      const clueName = clueDisplayName(m, p.clueId);
      showClueDiscoveryToast(scene, p.discovererDisplayName, clueName);
      appendDispatchMessage(
        formatClueDiscoveryDispatch(p.discovererDisplayName, clueName)
      );
    }
  );
  currentSocket?.off("game:waypoint");
  currentSocket?.on(
    "game:waypoint",
    (p: {
      userId?: string;
      clear?: boolean;
      wx?: number;
      wy?: number;
      label?: string;
      zone?: string;
    }) => {
      if (caseResolved || !p || p.userId === userId) return;
      if (p.clear) {
        game?.events.emit("ccc_partner_map_waypoint", null);
        return;
      }
      if (typeof p.wx !== "number" || typeof p.wy !== "number") return;
      game?.events.emit("ccc_partner_map_waypoint", {
        wx: p.wx,
        wy: p.wy,
        label: p.label,
        zone: p.zone,
      });
    }
  );
  currentSocket?.off("game:resolved");
  currentSocket?.on("game:resolved", (result: AccuseResult) => {
    closeAccusationOverlays();
    lastAccuseResult = result;
    showOutcome(result);
  });
  if (!alreadyResolved) startSessionPoll();
  } catch (err) {
    console.error("[ccc] startGame failed", err);
    await resetGameToLobby(
      "Could not start the case — check your connection and try again."
    );
  }
}

let outcomePanelReady = false;

function setupOutcomePanel(): void {
  if (outcomePanelReady) return;
  outcomePanelReady = true;
  el("outcome-close").onclick = () => dismissOutcome();
  el("outcome-overlay").addEventListener("click", (ev) => {
    if (ev.target === el("outcome-overlay")) dismissOutcome();
  });
}

function setManualOpen(manualEl: HTMLElement, open: boolean): void {
  if (!open) {
    releaseDomOverlayFocus(manualEl);
    focusPlayfieldCanvas();
    ensureGlobalCaptureEnabled();
  }
  manualEl.classList.toggle("open", open);
  manualEl.setAttribute("aria-hidden", open ? "false" : "true");
  setPlayerOverlayOpen("manual", open);
}

function setupSessionOverlays(
  manifest: CaseManifestV1,
  names: string[],
  phaserGame: Phaser.Game,
  lobbyPlayerCount: number
): void {
  sessionDomAbort?.abort();
  resetPlayerInputOverlays();
  sessionDomAbort = new AbortController();
  const { signal } = sessionDomAbort;

  const journalOverlay = el("journal-overlay");
  const manualEl = el("player-manual-overlay");
  const manualBody = el("player-manual-body");

  manualBody.innerHTML = buildPlayerManualHtml();
  setManualOpen(manualEl, false);

  el("player-manual-close").onclick = () => setManualOpen(manualEl, false);

  manualEl.addEventListener(
    "click",
    (ev) => {
      if (ev.target === manualEl) setManualOpen(manualEl, false);
    },
    { signal }
  );

  const tabs = [...JOURNAL_TAB_LABELS];
  const tabEl = el("journal-tabs");
  const body = el("journal-body");
  let active = 0;
  let mapBriefFooter = false;
  let briefAwaitingDismissPersist = false;

  let journalMirrorApply = false;
  let journalScrollMirrorTimer: ReturnType<typeof setTimeout> | null = null;

  const buildJournalMirrorPayload = (): import("@cold-case/shared/player-follow").LeaderMirrorJournal => ({
    kind: "journal",
    open: journalOverlay.classList.contains("open"),
    tab: active,
    mapViewZone: mapTabViewZone,
    journalScrollTop: body.scrollTop,
    mapPoiScrollTop:
      (document.getElementById("case-map-poi-scroll") as HTMLElement | null)?.scrollTop ?? 0,
    mapBuildingsCollapsed,
    mapNpcsCollapsed,
    manualScrollTop:
      (document.getElementById("player-manual-body") as HTMLElement | null)?.scrollTop ?? 0,
  });

  const pushLeaderMirrorToFollowers = (): void => {
    if (isFollowingLeader() || !leaderHasFollowers()) return;
    emitLeaderMirror({
      kind: "overlay",
      manualOpen: manualEl.classList.contains("open"),
      radioOpen: isRadioOverlayOpen(),
      radioTab: getRadioTab(),
    });
    if (journalOverlay.classList.contains("open")) {
      emitLeaderMirror(buildJournalMirrorPayload());
    }
  };

  setOnFollowersAttached(() => {
    pushLeaderMirrorToFollowers();
  });

  const scheduleJournalScrollMirror = (): void => {
    if (journalMirrorApply || isFollowingLeader() || !leaderHasFollowers()) return;
    if (journalScrollMirrorTimer) return;
    journalScrollMirrorTimer = setTimeout(() => {
      journalScrollMirrorTimer = null;
      emitLeaderMirror(buildJournalMirrorPayload());
    }, 100);
  };

  body.addEventListener("scroll", scheduleJournalScrollMirror, { signal, passive: true });
  const manualBodyEl = document.getElementById("player-manual-body");
  manualBodyEl?.addEventListener("scroll", scheduleJournalScrollMirror, {
    signal,
    passive: true,
  });

  const setJournalOpen = (open: boolean, opts?: { fromMirror?: boolean }): void => {
    if (open) {
      dismissNpcConversationUi();
      closeNarrativeModal();
      setManualOpen(manualEl, false);
      setRadioOpen(false);
      syncVisualViewportBounds();
    } else {
      clearMapBlipRefreshTimer();
      releaseDomOverlayFocus(journalOverlay);
      focusPlayfieldCanvas();
      ensureGlobalCaptureEnabled();
    }
    journalOverlay.classList.toggle("open", open);
    journalOverlay.classList.toggle(
      "journal-follower-readonly",
      isFollowingLeader() && open
    );
    setPlayerOverlayOpen("journal", open);
    phaserGame.events.emit("ccc_journal_open", open);
    if (isFollowingLeader()) {
      phaserGame.events.emit(
        "ccc_follower_hud_blocked",
        open || isNpcConversationOverlayOpen()
      );
    }
    if (!open) {
      if (
        shouldPersistBriefSeenOnJournalClose({
          activeTab: active,
          awaitingDismissPersist: briefAwaitingDismissPersist,
          fromMirror: opts?.fromMirror === true,
          sessionId,
        })
      ) {
        sessionStorage.setItem(briefSeenStorageKey(sessionId), "1");
        briefAwaitingDismissPersist = false;
      }
      mapBriefFooter = false;
      if (
        !opts?.fromMirror &&
        !caseResolved &&
        !investigationLocked &&
        !isAccusationPhaseActive()
      ) {
        signalPlayfieldReadyAfterBoot();
      }
    }
    if (!opts?.fromMirror && !isFollowingLeader() && leaderHasFollowers()) {
      emitLeaderMirror(buildJournalMirrorPayload());
    }
  };

  const setJournalTabFromMirror = (tab: number, opts?: { mapViewZone?: "downtown" | "maple_heights" }): void => {
    journalMirrorApply = true;
    active = Math.max(0, Math.min(tabs.length - 1, tab));
    if (opts?.mapViewZone) mapTabViewZone = opts.mapViewZone;
    renderTabBar();
    render();
    journalMirrorApply = false;
  };

  const onCloseJournalEvent = (): void => setJournalOpen(false);
  phaserGame.events.off("ccc_close_journal", onCloseJournalEvent);
  phaserGame.events.on("ccc_close_journal", onCloseJournalEvent);

  const toggleManual = (): void => {
    if (blockIfFollowingLeader()) return;
    if (isAccusationPhaseActive()) {
      showGameStatusMsg(accusationBlockedMessage());
      return;
    }
    const next = !manualEl.classList.contains("open");
    if (next) {
      dismissNpcConversationUi();
      closeNarrativeModal();
    }
    setManualOpen(manualEl, next);
    if (next) setJournalOpen(false);
    if (!isFollowingLeader() && leaderHasFollowers()) {
      emitLeaderMirror({
        kind: "overlay",
        manualOpen: next,
        radioOpen: isRadioOverlayOpen(),
        radioTab: getRadioTab(),
      });
    }
  };
  phaserGame.events.on("toggle_player_manual", toggleManual);

  const emitMapWaypoint = (p: MapWaypointTarget | null): void => {
    if (p && !isValidWorldWaypoint(p)) return;
    phaserGame.events.emit("ccc_map_waypoint", p);
  };

  const zoneName = (z: "downtown" | "maple_heights"): string => zoneDisplayName(manifest, z);
  const minEvidence =
    manifest.accusation_schema.minimum_evidence_count ?? MIN_EVIDENCE_FOR_ACCUSATION;

  initLeaderFollowMirror({
    getSessionId: () => sessionId,
    getSelfUserId: () => userId,
    getSocket: () => currentSocket,
  });
  setLeaderFollowMirrorSession(sessionId, currentSocket, userId);

  initPlayerFollow({
    getSessionId: () => sessionId,
    getSocket: () => currentSocket,
    getSelfUserId: () => userId,
    getPlayerCount: () => lobbyPlayerCount,
    getGameScene: () => phaserGame.scene.getScene("GameScene") as GameScene | undefined,
    labelForUserId: (uid) => {
      const row = sessionPlayersList.find((p) => p.userId === uid);
      if (!row) return "Detective";
      return worldLabelForPlayer(row, sessionPlayersList);
    },
    dismissLocalOverlays: () => {
      dismissNpcConversationUi();
      setJournalOpen(false);
      setManualOpen(manualEl, false);
      setRadioOpen(false);
      closeFollowConfirm();
      closeNarrativeModal();
      closeZoneTransitConfirm();
    },
    matchLeaderWorld: async ({ zone, buildingId }) => {
      const gs = phaserGame.scene.getScene("GameScene") as GameScene | undefined;
      if (!gs?.scene.isActive()) return;
      await gs.syncFollowLeaderWorld(zone, buildingId);
    },
    applyLeaderMirror: (mirror: LeaderMirrorPayload) => {
      if (mirror.kind === "journal") {
        if (mirror.open) {
          setJournalTabFromMirror(mirror.tab, { mapViewZone: mirror.mapViewZone });
          setJournalOpen(true, { fromMirror: true });
          journalMirrorApply = true;
          if (mirror.journalScrollTop != null) body.scrollTop = mirror.journalScrollTop;
          if (mirror.mapBuildingsCollapsed != null) {
            mapBuildingsCollapsed = mirror.mapBuildingsCollapsed;
          }
          if (mirror.mapNpcsCollapsed != null) {
            mapNpcsCollapsed = mirror.mapNpcsCollapsed;
          }
          const poiScroll = document.getElementById("case-map-poi-scroll");
          if (mirror.mapPoiScrollTop != null && poiScroll) {
            poiScroll.scrollTop = mirror.mapPoiScrollTop;
          }
          const buildingsDetails = document.getElementById("case-map-buildings");
          const npcsDetails = document.getElementById("case-map-npcs");
          if (buildingsDetails && mirror.mapBuildingsCollapsed != null) {
            buildingsDetails.open = !mirror.mapBuildingsCollapsed;
          }
          if (npcsDetails && mirror.mapNpcsCollapsed != null) {
            npcsDetails.open = !mirror.mapNpcsCollapsed;
          }
          const manualBody = document.getElementById("player-manual-body");
          if (mirror.manualScrollTop != null && manualBody) {
            manualBody.scrollTop = mirror.manualScrollTop;
          }
          journalMirrorApply = false;
        } else {
          setJournalOpen(false, { fromMirror: true });
        }
        return;
      }
      if (mirror.kind === "narrative") {
        if (mirror.open) {
          openNarrativeModal({
            title: mirror.title ?? "Investigation",
            leadText: mirror.leadText,
            bodyText: mirror.bodyText ?? "",
            buildingId: mirror.buildingId,
            readOnly: true,
            phaserGame,
          });
        } else {
          closeNarrativeModal();
        }
        return;
      }
      if (mirror.kind === "world") {
        void (async () => {
          const gs = phaserGame.scene.getScene("GameScene") as GameScene | undefined;
          await gs?.syncFollowLeaderWorld(mirror.zone, mirror.buildingId);
        })();
        return;
      }
      if (mirror.kind === "overlay") {
        if (mirror.manualOpen) {
          setManualOpen(manualEl, true);
        } else {
          setManualOpen(manualEl, false);
        }
        if (mirror.radioTab != null) setRadioTabFromMirror(mirror.radioTab);
        setRadioOpen(mirror.radioOpen, { fromMirror: true });
        return;
      }
      if (mirror.kind === "npc") {
        phaserGame.events.emit("ccc_follow_apply_npc_mirror", mirror);
      }
    },
    onFollowingChange: (following) => {
      phaserGame.events.emit("ccc_following_changed", following);
      if (following) {
        setJournalOpen(false, { fromMirror: true });
        setManualOpen(manualEl, false);
        setRadioOpen(false);
        closeNarrativeModal();
      } else {
        journalOverlay.classList.remove("journal-follower-readonly");
        phaserGame.events.emit("ccc_follower_hud_blocked", false);
        hideNarrativeNudgeUi();
      }
    },
    showStatus: showGameStatusMsg,
    onLeaderWorldChange: (zone, buildingId) => {
      const gs = phaserGame.scene.getScene("GameScene") as GameScene | undefined;
      void gs?.syncFollowLeaderWorld(zone, buildingId);
    },
  });
  rebindPlayerFollowSocket(currentSocket);

  phaserGame.events.on("ccc_stop_following", () => requestStopFollowing());
  phaserGame.events.on("ccc_game_status", (p: { text?: string; ms?: number }) => {
    if (p?.text) showGameStatusMsg(p.text, p.ms ?? 4000);
  });
  phaserGame.events.on("ccc_leader_mirror_npc", (raw: LeaderMirrorNpc) => {
    if (!leaderHasFollowers()) return;
    emitLeaderMirror({ ...raw, kind: "npc" });
  });

  initAccusationController({
    getSessionId: () => sessionId,
    getSocket: () => currentSocket,
    getPlayerCount: () => lobbyPlayerCount,
    getUserId: () => userId,
    getManifest: () => manifest,
    getJournal: () => journal,
    getPlayers: () => sessionPlayersList,
    getPlayerNames: () => names,
    zoneName,
    minEvidence,
    closeJournal: () => setJournalOpen(false),
    closeManual: () => setManualOpen(manualEl, false),
    closeRadio: () => setRadioOpen(false),
    dismissNpcUi: dismissNpcConversationUi,
    hideOutcome,
    onAccusationPhaseChange: (active, stage) => {
      recomputeSessionPollMode();
      game?.events.emit("ccc_accusation_arrest", stage === "arrest");
      game?.events.emit("ccc_accusation_phase_active", active);
    },
    onCancelled: (reason) => {
      recomputeSessionPollMode();
      const msg =
        reason === "vote_timeout" || reason === "vote_incomplete"
          ? "Accusation vote timed out or not everyone voted — back on the case."
          : "Accusation vote did not pass — back on the case.";
      showGameStatusMsg(msg, 7000);
      refreshJournalUi?.();
    },
    onStartError: (message) => {
      hideSessionLoading();
      showGameStatusMsg(message);
      refreshJournalUi?.();
    },
    onVotePassed: () => {
      showGameStatusMsg("Arrest phase — follow the steps on screen.", 6000);
    },
  });

  openJournalAtTab = (tab: number, opts?: { briefFooter?: boolean }): boolean => {
    if (blockIfFollowingLeader()) return false;
    if (isAccusationPhaseActive()) {
      showGameStatusMsg(accusationBlockedMessage());
      return false;
    }
    active = tab;
    mapBriefFooter = opts?.briefFooter ?? false;
    setJournalOpen(true);
    render();
    return journalOverlay.classList.contains("open");
  };

  const renderMapPoiListItem = (p: MapPoi, selectedId: string | null): string => {
    const btnClass = p.instanceId === selectedId ? "primary" : "secondary";
    const faded = !p.discovered && p.kind !== "transit" ? " map-poi-item--faded" : "";
    const displayLabel = mapPoiDisplayLabel(p);
    const labelBlock = p.isCrimeScene
      ? `<span class="map-poi-label"><em>${escapeHtml(displayLabel)}</em></span><span class="map-poi-crime-tag">(Crime scene)</span>`
      : `<span class="map-poi-label">${escapeHtml(displayLabel)}</span>`;
    return `<li class="map-poi-item${faded}"><button type="button" class="map-poi-btn ${btnClass}" data-id="${escapeHtml(p.instanceId)}">${labelBlock}<span class="map-poi-suffix">${escapeHtml(p.suffix)}</span></button></li>`;
  };

  const renderMapTab = (): void => {
    clearMapBlipRefreshTimer();
    const layerA = (phaserGame.registry.get("ccc") as { layerA?: LayerASpec } | undefined)
      ?.layerA;
    if (!layerA) {
      body.innerHTML = "<p>Map loading…</p>";
      return;
    }
    const viewZone = mapTabViewZone;
    const payload = buildMinimapPayload(manifest, layerA, journal, viewZone);
    const mapPlayerBlips = (): MinimapPlayerBlip[] => {
      const gs = phaserGame.scene.getScene("GameScene") as GameScene | undefined;
      return gs?.scene.isActive() ? gs.getMinimapPlayerBlips(viewZone) : [];
    };
    const allPois = buildMapPoiList(manifest, layerA, journal);
    const { buildings, npcs, transit } = partitionMapPois(allPois);
    const buildingsOpen = !mapBuildingsCollapsed;
    const npcsOpen = !mapNpcsCollapsed;
    const buildingRows = [...buildings, ...transit]
      .map((p) => renderMapPoiListItem(p, selectedMapPoiId))
      .join("");
    const npcRows = npcs.map((p) => renderMapPoiListItem(p, selectedMapPoiId)).join("");
    body.innerHTML = `<div class="case-map-zone-btns">
<button type="button" id="map-view-downtown" class="${viewZone === "downtown" ? "primary" : "secondary"}">Downtown</button>
<button type="button" id="map-view-maple" class="${viewZone === "maple_heights" ? "primary" : "secondary"}">Maple Heights</button>
</div>
<p class="case-map-hint">Viewing <strong>${escapeHtml(zoneName(viewZone))}</strong> on the map. Click a marker for a waypoint. Cross-zone targets route via transit. <span class="case-map-legend-npc-indoor">■</span> Orange = witness inside a building; red = street. <span class="case-map-legend-self">■</span> Blue = you; <span class="case-map-legend-peer">■</span> purple = teammate (<em>inside bldg</em> when indoors).</p>
<canvas id="case-map-canvas" width="520" height="360" class="case-map-canvas"></canvas>
<div id="case-map-poi-scroll" class="case-map-poi-scroll">
<details id="case-map-buildings" class="case-map-poi-section"${buildingsOpen ? " open" : ""}>
<summary>Buildings (${buildings.length + transit.length})</summary>
<ul class="case-map-poi-list">${buildingRows || '<li class="map-poi-empty">No buildings</li>'}</ul>
</details>
<details id="case-map-npcs" class="case-map-poi-section"${npcsOpen ? " open" : ""}>
<summary>Witnesses (${npcs.length})</summary>
<ul class="case-map-poi-list">${npcRows || '<li class="map-poi-empty">No witnesses</li>'}</ul>
</details>
</div>
<button type="button" id="case-map-clear" class="secondary case-map-clear-btn">Clear waypoint</button>`;
    const canvas = document.getElementById("case-map-canvas") as HTMLCanvasElement | null;
    if (!canvas) return;
    const repaintMapCanvas = (): void => {
      paintMinimapCanvas(canvas, payload, selectedMapPoiId, mapPlayerBlips());
    };
    const mirrorMapUiState = (): void => {
      if (journalMirrorApply || isFollowingLeader() || !leaderHasFollowers()) return;
      emitLeaderMirror(buildJournalMirrorPayload());
    };
    document.getElementById("map-view-downtown")?.addEventListener(
      "click",
      () => {
        mapTabViewZone = "downtown";
        render();
        mirrorMapUiState();
      },
      { signal }
    );
    document.getElementById("map-view-maple")?.addEventListener(
      "click",
      () => {
        mapTabViewZone = "maple_heights";
        render();
        mirrorMapUiState();
      },
      { signal }
    );
    const pickPoi = (p: MapPoi): void => {
      selectedMapPoiId = p.instanceId;
      const wp = resolveMapWaypoint(p, currentGameZone, layerA);
      if (!isValidWorldWaypoint(wp)) return;
      emitMapWaypoint(wp);
      if (sessionId) {
        currentSocket?.emit("game:waypoint", {
          sessionId,
          wx: wp.wx,
          wy: wp.wy,
          label: (wp.label ?? p.label).slice(0, 20),
          zone: currentGameZone,
        });
      }
      repaintMapCanvas();
      renderMapPoiList();
      const btn = document.querySelector(
        `#case-map-poi-scroll .map-poi-btn[data-id="${CSS.escape(p.instanceId)}"]`
      );
      btn?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    };
    const renderMapPoiList = (): void => {
      for (const p of allPois) {
        const btn = document.querySelector(
          `#case-map-poi-scroll .map-poi-btn[data-id="${CSS.escape(p.instanceId)}"]`
        ) as HTMLButtonElement | null;
        if (!btn) continue;
        const selected = p.instanceId === selectedMapPoiId;
        btn.classList.toggle("primary", selected);
        btn.classList.toggle("secondary", !selected);
      }
    };
    const poiScroll = document.getElementById("case-map-poi-scroll");
    poiScroll?.addEventListener("scroll", scheduleJournalScrollMirror, { signal, passive: true });
    const buildingsDetails = document.getElementById("case-map-buildings");
    const npcsDetails = document.getElementById("case-map-npcs");
    buildingsDetails?.addEventListener(
      "toggle",
      () => {
        mapBuildingsCollapsed = !buildingsDetails.open;
        mirrorMapUiState();
      },
      { signal }
    );
    npcsDetails?.addEventListener(
      "toggle",
      () => {
        mapNpcsCollapsed = !npcsDetails.open;
        mirrorMapUiState();
      },
      { signal }
    );
    poiScroll?.addEventListener(
      "click",
      (ev) => {
        const btn = (ev.target as HTMLElement).closest(".map-poi-btn") as
          | HTMLButtonElement
          | null;
        if (!btn?.dataset.id) return;
        const poi = allPois.find((x) => x.instanceId === btn.dataset.id);
        if (poi) pickPoi(poi);
      },
      { signal }
    );
    repaintMapCanvas();
    mapBlipRefreshTimer = setInterval(repaintMapCanvas, 300);
    canvas.addEventListener(
      "click",
      (ev) => {
        const rect = canvas.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) return;
        const x = ((ev.clientX - rect.left) / rect.width) * canvas.width;
        const y = ((ev.clientY - rect.top) / rect.height) * canvas.height;
        const best = findNearestMapPoiAtCanvas(payload, canvas.width, canvas.height, x, y);
        if (best) pickPoi(best);
      },
      { signal }
    );
    const clearBtn = document.getElementById("case-map-clear");
    clearBtn?.addEventListener(
      "click",
      () => {
        selectedMapPoiId = null;
        emitMapWaypoint(null);
        if (sessionId) {
          currentSocket?.emit("game:waypoint", { sessionId, clear: true });
        }
        render();
      },
      { signal }
    );
  };

  const onMapZoneChanged = (): void => {
    selectedMapPoiId = null;
    emitMapWaypoint(null);
    if (active === JOURNAL_MAP_TAB_INDEX) render();
  };

  const openJournalBrief = (): void => {
    if (blockIfFollowingLeader()) return;
    if (isAccusationPhaseActive()) {
      showGameStatusMsg(accusationBlockedMessage());
      return;
    }
    active = JOURNAL_BRIEF_TAB_INDEX;
    setJournalOpen(true);
    render();
  };
  const onOpenJournal = (): void => {
    openJournalBrief();
  };
  const onOpenJournalMap = (): void => {
    if (blockIfFollowingLeader()) return;
    if (isAccusationPhaseActive()) {
      showGameStatusMsg(accusationBlockedMessage());
      return;
    }
    active = JOURNAL_MAP_TAB_INDEX;
    mapTabViewZone = currentGameZone;
    setJournalOpen(true);
    render();
  };
  phaserGame.events.off("toggle_player_manual", toggleManual);
  phaserGame.events.off("ccc_map_zone_changed", onMapZoneChanged);
  phaserGame.events.off("ccc_open_journal", onOpenJournal);
  phaserGame.events.off("ccc_open_journal_map", onOpenJournalMap);
  phaserGame.events.on("toggle_player_manual", toggleManual);
  phaserGame.events.on("ccc_map_zone_changed", onMapZoneChanged);
  phaserGame.events.on("ccc_open_journal", onOpenJournal);
  phaserGame.events.on("ccc_open_journal_map", onOpenJournalMap);

  const openFirstBriefIfNeeded = (): boolean => {
    if (!sessionId || hasSeenCaseBrief(sessionId, sessionStorage)) return false;
    if (!openJournalAtTab) return false;
    const opened = openJournalAtTab(JOURNAL_BRIEF_TAB_INDEX, { briefFooter: true });
    if (opened) briefAwaitingDismissPersist = true;
    return opened;
  };
  ensureFirstCaseBriefOpened = () => {
    if (caseResolved || investigationLocked) return;
    if (openFirstBriefIfNeeded()) return;
    requestAnimationFrame(() => {
      if (caseResolved || investigationLocked) return;
      if (hasSeenCaseBrief(sessionId, sessionStorage)) return;
      if (!journalOverlay.classList.contains("open")) {
        openFirstBriefIfNeeded();
      }
    });
  };

  signal.addEventListener("abort", () => {
    briefAwaitingDismissPersist = false;
    phaserGame.events.off("toggle_player_manual", toggleManual);
    phaserGame.events.off("ccc_map_zone_changed", onMapZoneChanged);
    phaserGame.events.off("ccc_open_journal", onOpenJournal);
    phaserGame.events.off("ccc_open_journal_map", onOpenJournalMap);
    phaserGame.events.off("ccc_close_journal", onCloseJournalEvent);
    phaserGame.events.off("ccc_stop_following");
    phaserGame.events.off("ccc_game_status");
    phaserGame.events.off("ccc_leader_mirror_npc");
    disposePlayerFollow();
    disposeLeaderFollowMirror();
    setJournalOpen(false);
  });

  const renderTabBar = (): void => {
    active = Math.max(0, Math.min(tabs.length - 1, active));
    journalUiActiveTab = active;
    if (active !== JOURNAL_NOTES_TAB_INDEX) {
      lastNotesAgeRefreshMinute = -1;
    }
    tabEl.innerHTML = tabs
      .map((t, i) => {
        const mapClass = i === 0 ? " tab-map" : "";
        return `<div class="tab${mapClass} ${i === active ? "active" : ""}" data-i="${i}">${t}</div>`;
      })
      .join("");
  };

  tabEl.addEventListener(
    "click",
    (ev) => {
      const tab = (ev.target as HTMLElement).closest(".tab");
      if (!tab || !tabEl.contains(tab)) return;
      const i = Number((tab as HTMLElement).dataset.i);
      if (!Number.isFinite(i) || i === active) return;
      active = i;
      render();
      if (!journalMirrorApply && !isFollowingLeader() && leaderHasFollowers()) {
        emitLeaderMirror(buildJournalMirrorPayload());
      }
    },
    { signal }
  );

  const renderTabBody = (): void => {
    active = Math.max(0, Math.min(tabs.length - 1, active));
    if (active !== JOURNAL_MAP_TAB_INDEX) clearMapBlipRefreshTimer();
    if (active === JOURNAL_MAP_TAB_INDEX) {
      renderMapTab();
    } else if (active === JOURNAL_BRIEF_TAB_INDEX) {
      const briefFooter = mapBriefFooter
        ? '<p style="margin-top:1rem;padding-top:0.75rem;border-top:1px solid #2a3040;color:#98a2b8;font-size:0.9rem">(You can get here again by tapping Case Files in the lower left.)</p>'
        : "";
      body.innerHTML = renderCaseBriefHtml(manifest, names, {
        minEvidence,
        footerHtml: briefFooter,
      });
    } else if (active === JOURNAL_TIMELINE_TAB_INDEX) {
      const v = manifest.victim;
      const victimFoundAt = resolveFoundAtDisplay(manifest, v.found_at);
      body.innerHTML = `<p><strong>Time of death</strong><br/>${escapeHtml(displayOrUnknown(v.time_of_death))}</p>
<p><strong>Found at</strong><br/>${escapeHtml(victimFoundAt)}</p>
<p><strong>Cause</strong><br/>${escapeHtml(displayOrUnknown(v.cause_of_death))}</p>
<p><strong>Background</strong><br/>${escapeHtml(displayOrUnknown(v.backstory))}</p>`;
    } else if (active === JOURNAL_CLUES_TAB_INDEX) {
      const collapsed = readCollapsedClueIds(sessionId, userId);
      const list = (journal.discovered_clues as string[]) ?? [];
      const dc = new Set(list);
      const pinned = new Set((journal.pinned_clue_ids as string[]) ?? []);
      const sorted = [
        ...list.filter((id) => pinned.has(id)),
        ...list.filter((id) => !pinned.has(id)),
      ];
      const clueEntries = sorted
        .map((id) => {
          const c = manifest.clues.find((x) => x.clue_id === id);
          const pin = pinned.has(id) ? " ★" : "";
          const name = clueDisplayName(manifest, id);
          const foundAt = c ? resolveFoundAtDisplay(manifest, c.found_at) : "";
          const related = c ? relatedNpcDisplayName(manifest, c) : null;
          const relatedHtml = related
            ? `<p class="clue-meta"><strong>Related to</strong> ${escapeHtml(related)}</p>`
            : "";
          const npcThumb =
            c?.related_npc_instance_id &&
            manifest.npcs.find((n) => n.instance_id === c.related_npc_instance_id)
              ? npcSouthPortraitImgHtml(
                  manifest.npcs.find((n) => n.instance_id === c.related_npc_instance_id)!
                    .npc_id,
                  { sizePx: 40, alt: "" }
                )
              : "";
          const expanded = !collapsed.has(id);
          return `<div class="clue-entry${expanded ? " clue-entry--expanded" : ""}" data-clue-id="${escapeHtml(id)}">
<button type="button" class="clue-entry-toggle" aria-expanded="${expanded}">${npcThumb}<strong>${escapeHtml(name)}</strong>${pin}</button>
<div class="clue-entry-panel"${expanded ? "" : " hidden"}>
<p>${escapeHtml(c?.description ?? "")}</p>
<p class="clue-meta"><strong>Found at</strong> ${escapeHtml(foundAt)}</p>
${relatedHtml}
<button type="button" class="pin-clue" data-clue="${escapeHtml(id)}">${pinned.has(id) ? "Unpin" : "Pin"}</button>
</div></div>`;
        })
        .join("");
      let html = clueEntries
        ? `<div class="clue-list">${clueEntries}</div>`
        : "<p>No clues yet.</p>";
      const pairs = manifest.triangulation_pairs ?? [];
      const triDone = new Set((journal.triangulation_confirmed as string[]) ?? []);
      if (pairs.length) {
        html += "<h4>Triangulation</h4>";
        for (const pr of pairs) {
          if (triDone.has(pr.confirmed_clue_id)) {
            html += `<p>✓ ${escapeHtml(triangulationConfirmLabel(manifest, pr))}</p>`;
            continue;
          }
          const haveA = dc.has(pr.clue_a);
          const haveB = dc.has(pr.clue_b);
          if (haveA && haveB) {
            html += `<p><button type="button" class="tri-ok" data-ci="${escapeHtml(pr.confirmed_clue_id)}">Confirm: ${escapeHtml(triangulationConfirmLabel(manifest, pr))}</button></p><p style="font-size:12px">${escapeHtml(pr.confirmed_description)}</p>`;
          } else {
            const titleA = clueDisplayName(manifest, pr.clue_a);
            const titleB = clueDisplayName(manifest, pr.clue_b);
            html += `<p style="opacity:.75">Combine <strong>${escapeHtml(titleA)}</strong> + <strong>${escapeHtml(titleB)}</strong> to unlock.</p>`;
          }
        }
      }
      body.innerHTML = html;
      body.querySelectorAll(".clue-entry-toggle").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const entry = btn.closest(".clue-entry");
          const clueId = entry?.getAttribute("data-clue-id");
          if (!entry || !clueId) return;
          const panel = entry.querySelector(".clue-entry-panel");
          const nextCollapsed = entry.classList.contains("clue-entry--expanded");
          setClueCollapsed(sessionId, userId, clueId, nextCollapsed);
          entry.classList.toggle("clue-entry--expanded", !nextCollapsed);
          (btn as HTMLButtonElement).setAttribute("aria-expanded", String(!nextCollapsed));
          if (panel) (panel as HTMLElement).hidden = nextCollapsed;
        });
      });
      body.querySelectorAll("button.pin-clue").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const clueId = (btn as HTMLButtonElement).dataset.clue;
          if (!clueId) return;
          const ids = new Set((journal.pinned_clue_ids as string[]) ?? []);
          if (ids.has(clueId)) ids.delete(clueId);
          else ids.add(clueId);
          void patchJournal({ pinned_clue_ids: [...ids] });
        });
      });
      body.querySelectorAll("button.tri-ok").forEach((btn) => {
        btn.addEventListener("click", (e) => {
          e.stopPropagation();
          const cid = (btn as HTMLButtonElement).dataset.ci;
          if (!cid) return;
          const tc = new Set((journal.triangulation_confirmed as string[]) ?? []);
          tc.add(cid);
          const dc = new Set((journal.discovered_clues as string[]) ?? []);
          dc.add(cid);
          void patchJournal({
            triangulation_confirmed: [...tc],
            discovered_clues: [...dc],
          });
        });
      });
    } else if (active === JOURNAL_SUSPECTS_TAB_INDEX) {
      const metNpc = new Set((journal.talked_to_npcs as string[]) ?? []);
      const flagged = new Set(
        ((journal.flagged_suspects as { npc_instance_id: string }[]) ?? []).map(
          (f) => f.npc_instance_id
        )
      );
      const renderMetNpc = (n: (typeof manifest.npcs)[0]): string => {
        const roleLabel = npcRoleLabelForPlayer(n.role);
        const soloHint = n.solo_only ? " · personal lead" : "";
        const blurb =
          (n as { personality?: string }).personality ??
          (n as { personality_brief?: string }).personality_brief ??
          "";
        const flagMark = flagged.has(n.instance_id) ? " ★" : "";
        const npcLabel = npcDisplayName(manifest, n.instance_id);
        const thumb = npcSouthPortraitImgHtml(n.npc_id, { sizePx: 48, alt: npcLabel });
        return `<div class="suspect-row">${thumb}<div><p><strong>${escapeHtml(npcLabel)}</strong>${flagMark} <span style="opacity:.75">${escapeHtml(roleLabel)}${escapeHtml(soloHint)}</span><br/><span style="font-size:12px">${escapeHtml(blurb)}</span>
<button type="button" class="flag-suspect" data-npc="${escapeHtml(n.instance_id)}">${flagged.has(n.instance_id) ? "Unflag" : "Flag suspect"}</button></p></div></div>`;
      };
      const metList = manifest.npcs.filter((n) => metNpc.has(n.instance_id));
      const notMetCount = manifest.npcs.length - metList.length;
      let html =
        metList.map((n) => renderMetNpc(n)).join("") ||
        "<p>No interviews yet — talk to people in the world to fill this tab.</p>";
      if (notMetCount > 0) {
        html += `<details style="margin-top:12px"><summary>Not yet interviewed (${notMetCount})</summary>
<p style="margin:8px 0 0;line-height:1.45;opacity:.8;font-size:0.92rem">You have not opened case notes for these people yet. Names and roles stay hidden until you investigate them in the world.</p></details>`;
      }
      body.innerHTML = html || "<p>No suspects in this case.</p>";
      body.querySelectorAll("button.flag-suspect").forEach((btn) => {
        btn.addEventListener("click", () => {
          const npcId = (btn as HTMLButtonElement).dataset.npc;
          if (!npcId) return;
          const list = [...((journal.flagged_suspects as { npc_instance_id: string; reason: string }[]) ?? [])];
          const idx = list.findIndex((f) => f.npc_instance_id === npcId);
          if (idx >= 0) list.splice(idx, 1);
          else list.push({ npc_instance_id: npcId, reason: "flagged" });
          void patchJournal({ flagged_suspects: list });
        });
      });
    } else if (active === JOURNAL_NOTES_TAB_INDEX) {
      lastNotesAgeRefreshMinute = Math.floor(investigationElapsedSec / 60);
      const composeDraft =
        (document.getElementById("notes-compose") as HTMLTextAreaElement | null)?.value ?? "";
      const notes = [...((journal.case_notes as CaseNoteEntry[]) ?? [])].sort(
        (a, b) => caseNoteSortKey(b) - caseNoteSortKey(a)
      );
      const feed =
        notes
          .map(
            (n) =>
              `<article class="case-note" data-note-id="${escapeHtml(n.id)}"><p class="case-note-meta"><strong>${escapeHtml(n.displayName)}</strong> · ${escapeHtml(formatCaseNoteAge(n, investigationElapsedSec))}</p><p>${escapeHtml(n.text)}</p></article>`
          )
          .join("") || `<p style="opacity:.8">No team notes yet. Post the first theory below.</p>`;
      body.innerHTML = `<p style="font-size:0.9rem;color:#8892a8">Shared notes — everyone in this case sees them.</p>
<div class="case-notes-feed">${feed}</div>
<textarea id="notes-compose" rows="4" placeholder="Add a note for your team…" style="width:100%;box-sizing:border-box;background:#0e0e18;color:#e4e8f4;border:1px solid #3a4560;border-radius:8px;padding:10px;font:inherit;line-height:1.45;margin-top:12px"></textarea>
<button type="button" id="notes-post" class="primary" style="margin-top:8px">Post note</button>`;
      document.getElementById("notes-post")?.addEventListener("click", () => {
        const text = (
          document.getElementById("notes-compose") as HTMLTextAreaElement | null
        )?.value.trim();
        if (!text) return;
        const ccc = phaserGame.registry.get("ccc") as { playerName?: string } | undefined;
        const entry: CaseNoteEntry = {
          id: crypto.randomUUID(),
          userId,
          displayName: ccc?.playerName ?? "Detective",
          text: text.slice(0, 20_000),
          createdAtMs: Date.now(),
          createdAtElapsedSec: investigationElapsedSec,
        };
        lastNotesAgeRefreshMinute = -1;
        void patchJournal({ case_notes_append: [entry] });
        const ta = document.getElementById("notes-compose") as HTMLTextAreaElement | null;
        if (ta) ta.value = "";
      });
      const composeTa = document.getElementById("notes-compose") as HTMLTextAreaElement | null;
      if (composeTa && composeDraft) composeTa.value = composeDraft;
      composeTa?.addEventListener("keydown", (e) => {
        e.stopPropagation();
      });
      composeTa?.addEventListener("keyup", (e) => e.stopPropagation());
      composeTa?.addEventListener("keypress", (e) => e.stopPropagation());
    } else if (active === JOURNAL_ACCUSE_TAB_INDEX) {
      if (caseResolved) {
        body.innerHTML = `<p>This case is closed.</p>
<button type="button" id="btn-view-outcome" class="primary">View result</button>`;
        document.getElementById("btn-view-outcome")?.addEventListener("click", () => {
          if (lastAccuseResult) displayOutcomePanel(lastAccuseResult);
        });
        return;
      }
      const clueCount = (journal.discovered_clues as string[])?.length ?? 0;
      const can = clueCount >= minEvidence;
      const metNpcEarly = new Set((journal.talked_to_npcs as string[]) ?? []);
      if (lobbyPlayerCount >= 2) {
        body.innerHTML = renderMultiplayerAccuseTabHtml(
          lobbyPlayerCount,
          can,
          clueCount,
          minEvidence,
          isAccusationPhaseActive(),
          metNpcEarly.size
        );
        document.getElementById("btn-start-accusation")?.addEventListener("click", () => {
          startAccusationFromCaseFile();
        });
        return;
      }
      const metNpc = new Set((journal.talked_to_npcs as string[]) ?? []);
      const ruledOut = new Set((journal.ruled_out_npc_ids as string[]) ?? []);
      const allMet = manifest.npcs.filter((n) => metNpc.has(n.instance_id));
      const prevAccK = document.getElementById("acc-k") as HTMLInputElement | null;
      let selectedKiller =
        prevAccK?.value && !ruledOut.has(prevAccK.value)
          ? prevAccK.value
          : (allMet.find((n) => !ruledOut.has(n.instance_id))?.instance_id ?? "");
      if (!allMet.length) {
        body.innerHTML = `<div class="acc-phase-callout">
<p><strong>Before you can accuse</strong></p>
<p>Interview at least <strong>one suspect</strong> in the world (case notes → Begin interview).</p>
</div>`;
        return;
      }
      const accCards = allMet
        .map((n) => {
          const out = ruledOut.has(n.instance_id);
          const sel = n.instance_id === selectedKiller && !out;
          const npcLabel = npcDisplayName(manifest, n.instance_id);
          const thumb = npcSouthPortraitImgHtml(n.npc_id, { sizePx: 64, alt: npcLabel });
          return `<div class="acc-suspect-card${out ? " ruled-out" : ""}${sel ? " selected" : ""}" data-npc="${escapeHtml(n.instance_id)}">${thumb}<p class="acc-suspect-name">${escapeHtml(npcLabel)}</p><button type="button" class="acc-rule-out" data-npc="${escapeHtml(n.instance_id)}">✕</button></div>`;
        })
        .join("");
      body.innerHTML = can
        ? `<p>Tap a portrait to accuse who you believe is responsible. <strong>✕</strong> rules them out for the team.</p>
        <div class="acc-suspect-grid">${accCards}</div>
        <input type="hidden" id="acc-k" value="${escapeHtml(selectedKiller)}"/>
        <button type="button" id="btn-accuse">Request warrant</button>`
        : `<div class="acc-phase-callout">
<p><strong>Before you can accuse</strong></p>
<p>Discover <strong>${minEvidence - clueCount}</strong> more clue${minEvidence - clueCount === 1 ? "" : "s"} (you have ${clueCount}, need ${minEvidence}).</p>
</div>`;
      body.querySelectorAll(".acc-suspect-card").forEach((card) => {
        card.addEventListener("click", (ev) => {
          if ((ev.target as HTMLElement).closest(".acc-rule-out")) return;
          const id = (card as HTMLElement).dataset.npc;
          if (!id || ruledOut.has(id)) return;
          selectedKiller = id;
          const hidden = document.getElementById("acc-k") as HTMLInputElement | null;
          if (hidden) hidden.value = id;
          render();
        });
      });
      body.querySelectorAll(".acc-rule-out").forEach((btnEl) => {
        btnEl.addEventListener("click", (ev) => {
          ev.stopPropagation();
          const id = (btnEl as HTMLElement).dataset.npc;
          if (!id) return;
          const next = new Set((journal.ruled_out_npc_ids as string[]) ?? []);
          if (next.has(id)) next.delete(id);
          else next.add(id);
          if (selectedKiller === id) {
            selectedKiller = allMet.find((n) => !next.has(n.instance_id))?.instance_id ?? "";
          }
          void patchJournal({ ruled_out_npc_ids: [...next] });
        });
      });
      const btn = document.getElementById("btn-accuse") as HTMLButtonElement | null;
      btn?.addEventListener("click", async () => {
        if (!btn || btn.disabled) return;
        const killer = (document.getElementById("acc-k") as HTMLInputElement)?.value ?? "";
        if (!killer || ruledOut.has(killer)) {
          showGameStatusMsg("Select who you believe is responsible.");
          return;
        }
        btn.disabled = true;
        const res = await api<AccuseResult>(`/api/sessions/${sessionId}/accuse`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            killer_instance_id: killer,
            evidence_clue_ids: [],
            investigation_elapsed_seconds: investigationElapsedSec,
          }),
        });
        btn.disabled = false;
        if (!res.ok) {
          if ((res.data as { error?: string })?.error === "game_over") {
            await recoverSessionOutcome();
          } else {
            showGameStatusMsg(formatApiError(res.data, "Accusation failed — try again."));
          }
          return;
        }
        clearRejoinHint();
        showOutcome(res.data as AccuseResult);
        setJournalOpen(false);
      });
    }
  };

  const render = (): void => {
    renderTabBar();
    renderTabBody();
  };

  refreshJournalUi = () => {
    if (!journalOverlay.classList.contains("open")) return;
    if (active === JOURNAL_MAP_TAB_INDEX) renderMapTab();
    else renderTabBody();
  };
  render();

  el("journal-close").onclick = () => setJournalOpen(false);

  window.addEventListener(
    "keydown",
    (e) => {
      if (e.repeat) return;
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;

      const manualOpen = manualEl.classList.contains("open");
      const radioOpen = isRadioOverlayOpen();
      if (e.key === "Escape") {
        if (isNarrativeModalOpen()) {
          closeNarrativeModal();
          e.preventDefault();
          return;
        }
        if (manualOpen) {
          setManualOpen(manualEl, false);
          e.preventDefault();
          return;
        }
        if (radioOpen) {
          setRadioOpen(false);
          e.preventDefault();
          return;
        }
        if (journalOverlay.classList.contains("open")) {
          setJournalOpen(false);
          e.preventDefault();
        }
        return;
      }
      if (e.key === "F1") {
        e.preventDefault();
        toggleManual();
        return;
      }
      if (e.key === "?" || (e.shiftKey && e.code === "Slash")) {
        toggleManual();
        return;
      }
      if (manualOpen) return;
      if (radioOpen) return;
      if (e.key === "j" || e.key === "J") {
        openJournalBrief();
        return;
      }
      if (e.key === "m" || e.key === "M") {
        onOpenJournalMap();
      }
    },
    { signal }
  );
}

window.addEventListener("DOMContentLoaded", () => {
  ensureConnectionLifecycleWired();
  initPreGameApp();
  if (getToken()) {
    void showSignedInHub().then(() => offerResumePromptIfNeeded());
  } else {
    showLanding();
  }
});
